import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { API_BASE, SOCKET_URL, SOCKET_PATH, formatCurrency, formatDateShort, getImageUrl } from '../config/api';
import Swal from 'sweetalert2';
import RecipeCalculator from './RecipeCalculator';
import BatchBillPrintModal from './BatchBillPrintModal';
import FlavorChangeModal, { customerMessage } from './FlavorChangeModal';
import { isFullyPaid, isPartial, paidAmountOf, amountDueOf, refundDueOf, canChangeFlavor, money } from '../billPayment';
import useOrderStatus from '../hooks/useOrderStatus';
import './AdminPage.css';

const CHANGE_LOG_LABELS = {
    flavor_change: 'Đổi vị',
    payment_received: 'Nhận chuyển khoản',
    collected: 'Đã thu chênh lệch',
    refunded: 'Đã hoàn tiền',
};

export default function AdminPage() {
    const [currentTab, setCurrentTab] = useState(() => {
        const query = new URLSearchParams(window.location.search);
        return query.get('tab') || 'schedule';
    }); // 'schedule', 'batch', 'orders', 'products', 'sepay', 'recipe'
    const [bills, setBills] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter & Search
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchOrderQuery, setSearchOrderQuery] = useState('');

    // Batch Production (Theo mẻ) states
    const [batchDateFilter, setBatchDateFilter] = useState('all');
    const [batchSlotFilter, setBatchSlotFilter] = useState('all');
    const [batchStatusFilter, setBatchStatusFilter] = useState('all_active');
    const [batchViewMode, setBatchViewMode] = useState('cakes'); // 'cakes' or 'fillings'
    const [batchWeightFilter, setBatchWeightFilter] = useState('all'); // 'all', '150g', '180g'
    const [batchOrderFilter, setBatchOrderFilter] = useState('all'); // 'all' or bill.orderCode
    const [batchFillingFilter, setBatchFillingFilter] = useState('all'); // 'all' or filling name
    const [completedCakes, setCompletedCakes] = useState({});

    // SePay Config
    const [sepayConfig, setSepayConfig] = useState({
        bankShortName: 'MB',
        accountNumber: '0398176703',
        accountHolder: 'VU THE HAI',
        // Never ship a webhook secret in the bundle — it is served to every visitor.
        // The real value is loaded from GET /payment/config when the admin page opens.
        webhookToken: ''
    });
    const [savingSepay, setSavingSepay] = useState(false);

    // Product Modal state
    const [showProductModal, setShowProductModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [uploadingImg, setUploadingImg] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [productForm, setProductForm] = useState({
        name: '',
        category: 'Bánh nướng',
        description: '',
        filling: '',
        price: '',
        imageUrl: '',
        stock_status: 'stock'
    });

    // Detail modal state
    const [detailBill, setDetailBill] = useState(null);

    // Flavour changes & the price-difference ledger
    const [flavorBill, setFlavorBill] = useState(null);
    const [changeLog, setChangeLog] = useState([]);
    const [settling, setSettling] = useState(false);

    // Batch Bill Print Modal state
    const [showBatchPrintModal, setShowBatchPrintModal] = useState(false);
    const [batchPrintDate, setBatchPrintDate] = useState('all');
    const [batchPrintSingleBill, setBatchPrintSingleBill] = useState(null);

    // Authentication State (tk: admin / mk: Admin@123)
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        try {
            return Boolean(localStorage.getItem('admin_auth_token') || sessionStorage.getItem('admin_auth_token'));
        } catch (e) {
            return false;
        }
    });
    const [loginUser, setLoginUser] = useState('');
    const [loginPass, setLoginPass] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [loginError, setLoginError] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        setLoginError('');
        const trimmedUser = loginUser.trim();
        if (!trimmedUser || !loginPass) {
            setLoginError('Vui lòng nhập đầy đủ tên tài khoản và mật khẩu!');
            return;
        }

        setIsLoggingIn(true);
        try {
            const res = await axios.post(`${API_BASE}/admin/login`, {
                username: trimmedUser,
                password: loginPass
            }).catch(err => err.response || { status: 500 });

            const isApiSuccess = res?.data?.success === true;
            const isLocalValid = trimmedUser.toLowerCase() === 'admin' && loginPass === 'Admin@123';

            if (isApiSuccess || isLocalValid) {
                const token = res?.data?.token || 'admin_token_' + Date.now();
                if (rememberMe) {
                    localStorage.setItem('admin_auth_token', token);
                    localStorage.setItem('admin_user', trimmedUser);
                } else {
                    sessionStorage.setItem('admin_auth_token', token);
                    sessionStorage.setItem('admin_user', trimmedUser);
                }
                setIsAuthenticated(true);
                Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 })
                    .fire({ icon: 'success', title: 'Đăng nhập thành công! Chào mừng Quản trị viên.' });
            } else {
                setLoginError(res?.data?.message || 'Tài khoản hoặc mật khẩu không chính xác!');
            }
        } catch (err) {
            if (trimmedUser.toLowerCase() === 'admin' && loginPass === 'Admin@123') {
                const token = 'admin_token_' + Date.now();
                if (rememberMe) {
                    localStorage.setItem('admin_auth_token', token);
                    localStorage.setItem('admin_user', trimmedUser);
                } else {
                    sessionStorage.setItem('admin_auth_token', token);
                    sessionStorage.setItem('admin_user', trimmedUser);
                }
                setIsAuthenticated(true);
            } else {
                setLoginError('Tài khoản hoặc mật khẩu không chính xác!');
            }
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleLogout = () => {
        Swal.fire({
            title: 'Đăng xuất?',
            text: 'Bạn có chắc chắn muốn đăng xuất khỏi bảng quản trị?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#142C54',
            cancelButtonColor: '#7A6A5F',
            confirmButtonText: 'Đăng xuất',
            cancelButtonText: 'Ở lại'
        }).then((res) => {
            if (res.isConfirmed) {
                localStorage.removeItem('admin_auth_token');
                localStorage.removeItem('admin_user');
                sessionStorage.removeItem('admin_auth_token');
                sessionStorage.removeItem('admin_user');
                setIsAuthenticated(false);
                setLoginPass('');
                Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2500 })
                    .fire({ icon: 'info', title: 'Đã đăng xuất khỏi hệ thống.' });
            }
        });
    };

    // Order Intake On/Off state & helpers
    const { orderStatus, orderEnabled, toggleOrderStatus, updateOrderStatus } = useOrderStatus();
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [savingOrderStatus, setSavingOrderStatus] = useState(false);
    const [orderForm, setOrderForm] = useState({
        orderEnabled: true,
        closedTitle: '',
        closedReason: '',
        reopenTime: '',
        hotline: '',
        zaloLink: ''
    });

    const handleOpenOrderSettingsModal = () => {
        setOrderForm({
            orderEnabled: orderStatus.orderEnabled,
            closedTitle: orderStatus.closedTitle || 'Tiệm Tạm Dừng Nhận Đơn Mới',
            closedReason: orderStatus.closedReason || '',
            reopenTime: orderStatus.reopenTime || '',
            hotline: orderStatus.hotline || '0856.345.789',
            zaloLink: orderStatus.zaloLink || 'https://zalo.me/0856345789'
        });
        setShowOrderModal(true);
    };

    const handleToggleOrderIntake = async () => {
        const next = !orderEnabled;
        const res = await toggleOrderStatus(next);
        if (res.success) {
            Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3500 })
                .fire({
                    icon: next ? 'success' : 'warning',
                    title: next ? '🟢 Đã MỞ nhận đơn mới!' : '🔴 Đã TẠM ĐÓNG nhận đơn mới!'
                });
        } else {
            Swal.fire({ icon: 'error', title: 'Lỗi', text: res.error || 'Không thể cập nhật trạng thái nhận đơn' });
        }
    };

    const handleSaveOrderSettings = async (e) => {
        if (e) e.preventDefault();
        setSavingOrderStatus(true);
        try {
            const res = await updateOrderStatus(orderForm);
            if (res.success) {
                setShowOrderModal(false);
                Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 })
                    .fire({ icon: 'success', title: 'Đã lưu cấu hình nhận đơn thành công!' });
            } else {
                Swal.fire({ icon: 'error', title: 'Lỗi', text: res.error || 'Không thể lưu cài đặt' });
            }
        } finally {
            setSavingOrderStatus(false);
        }
    };

    const handleOpenBatchPrint = (dateStr = 'all', singleBill = null) => {
        setBatchPrintDate(dateStr);
        setBatchPrintSingleBill(singleBill);
        setShowBatchPrintModal(true);
    };

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [billsRes, productsRes, sepayRes] = await Promise.all([
                axios.get(`${API_BASE}/bill/getListBill`),
                axios.get(`${API_BASE}/product/getProduct`),
                axios.get(`${API_BASE}/payment/config`).catch(() => ({ data: null }))
            ]);

            if (billsRes.data && Array.isArray(billsRes.data)) {
                setBills(billsRes.data);
            }
            if (productsRes.data && productsRes.data.data) {
                setProducts(productsRes.data.data);
            }
            if (sepayRes.data) {
                setSepayConfig(prev => ({ ...prev, ...sepayRes.data }));
            }
        } catch (err) {
            console.error('Lỗi tải dữ liệu admin:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchAllData();
        }
    }, [isAuthenticated]);

    // Realtime: the backend already emits these, nothing on the admin side was listening,
    // so a new order or an auto-settled transfer only showed up after a manual refresh.
    useEffect(() => {
        if (!isAuthenticated) return;
        const socket = io(SOCKET_URL, {
            path: SOCKET_PATH,
            transports: ['websocket', 'polling'],
            withCredentials: true,
        });

        const sameBill = (bill, data) => (
            (data.id != null && String(bill.id) === String(data.id)) ||
            (data.orderCode && bill.orderCode === data.orderCode)
        );

        const applyStatus = (data) => {
            if (!data) return;
            const merge = (b) => ({
                ...b,
                ...(data.status ? { status: data.status } : {}),
                ...(data.paymentStatus ? { paymentStatus: data.paymentStatus } : {}),
                ...('paidAmount' in data ? { paidAmount: data.paidAmount } : {}),
            });
            setBills(prev => prev.map(b => (sameBill(b, data) ? merge(b) : b)));
            setDetailBill(prev => (prev && sameBill(prev, data) ? merge(prev) : prev));
        };

        socket.on('billChanged', (bill) => {
            if (!bill || bill.id == null) return;
            setBills(prev => prev.map(b => (String(b.id) === String(bill.id) ? { ...b, ...bill } : b)));
            setDetailBill(prev => (prev && String(prev.id) === String(bill.id) ? { ...prev, ...bill } : prev));
        });

        socket.on('addNewBill', (bill) => {
            if (!bill || bill.id == null) return;
            setBills(prev => (prev.some(b => String(b.id) === String(bill.id)) ? prev : [bill, ...prev]));
            Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 4000 })
                .fire({ icon: 'info', title: `Đơn mới #${bill.orderCode || bill.id}` });
        });

        socket.on('updateStatusBill', applyStatus);
        socket.on('paymentSuccess', (data) => {
            applyStatus({ ...(data || {}), status: 'Đã thanh toán', paymentStatus: 'paid' });
            if (data) {
                Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 5000 })
                    .fire({ icon: 'success', title: `Đã nhận thanh toán #${data.orderCode || data.id}` });
            }
        });

        socket.on('paymentUnderpaid', (data) => {
            if (!data) return;
            Swal.fire({
                icon: 'warning',
                title: 'Chuyển khoản thiếu tiền',
                html: `Đơn <strong>#${data.orderCode || data.id}</strong> nhận <strong>${formatCurrency(data.receivedAmount)}</strong>`
                    + ` / cần <strong>${formatCurrency(data.expectedAmount)}</strong>.<br/>Đơn giữ nguyên trạng thái, cần kiểm tra thủ công.`,
                confirmButtonColor: '#142C54',
            });
        });

        socket.on('updateDeliveryDate', (data) => {
            if (!data) return;
            setBills(prev => prev.map(b => (sameBill(b, data) ? {
                ...b,
                delivery_date: data.delivery_date ?? b.delivery_date,
                deliveryTimeSlot: data.deliveryTimeSlot ?? b.deliveryTimeSlot,
            } : b)));
        });

        socket.on('billDeleted', () => { fetchAllData(); });

        return () => socket.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Status change
    const handleStatusChange = async (billId, newStatus) => {
        try {
            const res = await axios.put(`${API_BASE}/bill/editStatusBill/${billId}`, { status: newStatus });
            const data = res.data?.data || {};
            const patch = {
                status: newStatus,
                ...(data.paymentStatus ? { paymentStatus: data.paymentStatus } : {}),
                ...('paidAmount' in data ? { paidAmount: data.paidAmount } : {}),
            };
            setBills(prev => prev.map(b => b.id === billId ? { ...b, ...patch } : b));
            setDetailBill(prev => (prev && prev.id === billId ? { ...prev, ...patch } : prev));
            const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            Toast.fire({ icon: 'success', title: 'Cập nhật trạng thái thành công' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Không thể cập nhật trạng thái đơn!' });
        }
    };

    // Keep the list and an open detail modal on the same copy of a bill.
    const replaceBill = (updated) => {
        if (!updated || updated.id == null) return;
        setBills(prev => prev.map(b => (String(b.id) === String(updated.id) ? { ...b, ...updated } : b)));
        setDetailBill(prev => (prev && String(prev.id) === String(updated.id) ? { ...prev, ...updated } : prev));
    };

    // Outstanding flavour-change difference, or an overpayment to hand back.
    const renderPayTag = (b) => {
        const due = isPartial(b) ? amountDueOf(b) : 0;
        const refund = refundDueOf(b);
        if (due > 0) return <div className="pay-tag due">Còn thu {formatCurrency(due)}</div>;
        if (refund > 0) return <div className="pay-tag refund">Cần hoàn {formatCurrency(refund)}</div>;
        return null;
    };

    useEffect(() => {
        if (!detailBill?.id) {
            setChangeLog([]);
            return undefined;
        }
        let alive = true;
        axios.get(`${API_BASE}/bill/changeLog/${detailBill.id}`)
            .then(res => { if (alive) setChangeLog(res.data?.entries || []); })
            .catch(() => { if (alive) setChangeLog([]); });
        return () => { alive = false; };
    }, [detailBill?.id, detailBill?.totalAmount, detailBill?.paidAmount, detailBill?.paymentStatus]);

    const handleSettle = async (action) => {
        if (!detailBill || settling) return;
        const collecting = action === 'collected';
        const amount = collecting ? amountDueOf(detailBill) : refundDueOf(detailBill);

        const confirm = await Swal.fire({
            icon: 'question',
            title: collecting ? 'Xác nhận đã thu tiền chênh lệch?' : 'Xác nhận đã hoàn tiền cho khách?',
            html: `Đơn <strong>#${detailBill.orderCode}</strong> · <strong>${formatCurrency(amount)}</strong>`,
            input: 'text',
            inputPlaceholder: collecting ? 'Ghi chú, VD: thu tiền mặt khi giao' : 'Ghi chú, VD: đã CK hoàn qua MB',
            showCancelButton: true,
            confirmButtonText: 'Xác nhận',
            cancelButtonText: 'Huỷ',
            confirmButtonColor: '#142C54',
        });
        if (!confirm.isConfirmed) return;

        setSettling(true);
        try {
            // Pin the action to the amount the admin just confirmed; if the order moved
            // meanwhile (a transfer landed, another admin acted), show the new numbers instead.
            const fresh = await axios.get(`${API_BASE}/bill/version/${detailBill.id}`);
            const freshBill = fresh.data.bill;
            const freshAmount = collecting ? amountDueOf(freshBill) : refundDueOf(freshBill);
            if (freshAmount !== amount) {
                replaceBill(freshBill);
                Swal.fire({
                    icon: 'info',
                    title: 'Số tiền vừa thay đổi',
                    text: `Đơn vừa được cập nhật ở nơi khác — số cần ${collecting ? 'thu' : 'hoàn'} hiện là ${formatCurrency(freshAmount)}. Vui lòng kiểm tra lại.`,
                    confirmButtonColor: '#142C54',
                });
                return;
            }

            const res = await axios.post(`${API_BASE}/bill/settleDifference/${detailBill.id}`, {
                action,
                version: fresh.data.version,
                note: confirm.value || '',
            });
            replaceBill(res.data.bill);
            Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2500 })
                .fire({ icon: 'success', title: collecting ? 'Đã ghi nhận thu đủ tiền' : 'Đã ghi nhận hoàn tiền' });
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Không lưu được',
                text: err.response?.data?.message || 'Lỗi máy chủ, đơn hàng chưa bị thay đổi.',
                confirmButtonColor: '#142C54',
            });
        } finally {
            setSettling(false);
        }
    };

    const handleFlavorApplied = (before, updated, result) => {
        replaceBill(updated);
        setFlavorBill(null);

        const message = customerMessage({ ...before, ...updated }, result, sepayConfig);
        const moneyLine = !result.receivedMoney
            ? `Tổng mới <strong>${formatCurrency(result.newTotal)}</strong> — khách chưa thanh toán, sẽ trả theo tổng mới.`
            : result.amountDue > 0
                ? `Khách cần trả thêm <strong>${formatCurrency(result.amountDue)}</strong>.`
                : result.refundDue > 0
                    ? `Cần hoàn lại khách <strong>${formatCurrency(result.refundDue)}</strong>.`
                    : 'Không phát sinh chênh lệch tiền.';

        Swal.fire({
            icon: 'success',
            title: 'Đã đổi vị',
            html: `${moneyLine}<br/><small style="color:#6B5A4E">Nếu đã in bill cho đơn này, hãy in lại để bill và mã QR đúng số tiền mới.</small>`,
            showCancelButton: true,
            confirmButtonText: 'Sao chép tin nhắn cho khách',
            cancelButtonText: 'Đóng',
            confirmButtonColor: '#142C54',
        }).then((r) => {
            if (!r.isConfirmed) return;
            const showText = () => Swal.fire({ title: 'Tin nhắn cho khách', input: 'textarea', inputValue: message, confirmButtonColor: '#142C54' });
            if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(message)
                    .then(() => Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 })
                        .fire({ icon: 'success', title: 'Đã sao chép tin nhắn' }))
                    .catch(showText);
            } else {
                showText();
            }
        });
    };

    // Delete bill
    const handleDeleteBill = (billId) => {
        Swal.fire({
            title: 'Xoá đơn hàng này?',
            text: 'Hành động này không thể hoàn tác!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#D32F2F',
            cancelButtonColor: '#7A6A5F',
            confirmButtonText: 'Xoá vĩnh viễn'
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axios.delete(`${API_BASE}/bill/deleteBill/${billId}`);
                    setBills(prev => prev.filter(b => b.id !== billId));
                    Swal.fire({ icon: 'success', title: 'Đã xoá đơn hàng' });
                } catch (err) {
                    Swal.fire({ icon: 'error', title: 'Lỗi xoá đơn' });
                }
            }
        });
    };

    // Delete all test bills (Mã TEST*)
    const handleDeleteTestBills = () => {
        Swal.fire({
            title: 'Xoá toàn bộ đơn test?',
            text: 'Hệ thống sẽ xoá tất cả các đơn hàng thử nghiệm (mã TEST*) để sẵn sàng phục vụ khách hàng thật!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#D32F2F',
            cancelButtonColor: '#7A6A5F',
            confirmButtonText: 'Đồng ý xoá đơn test',
            cancelButtonText: 'Hủy'
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    const response = await axios.delete(`${API_BASE}/bill/deleteTestBills`);
                    Swal.fire({
                        icon: 'success',
                        title: 'Đã xoá toàn bộ đơn test!',
                        text: `Đã dọn dẹp các đơn thử nghiệm (${response.data.affectedRows || ''} đơn). Sẵn sàng mở bán!`
                    });
                    fetchAllData();
                } catch (err) {
                    Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Không thể xoá đơn test!' });
                }
            }
        });
    };

    // Save SePay config
    const handleSaveSepayConfig = async (e) => {
        e.preventDefault();
        setSavingSepay(true);
        try {
            await axios.put(`${API_BASE}/payment/config`, sepayConfig);
            Swal.fire({ icon: 'success', title: 'Thành công', text: 'Đã cập nhật cấu hình SePay VietQR!' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Không thể lưu cấu hình SePay!' });
        } finally {
            setSavingSepay(false);
        }
    };

    // Product form handling
    const handleOpenProductModal = (prod = null) => {
        setPreviewUrl('');
        if (prod) {
            setEditingProduct(prod);
            setProductForm({
                name: prod.name || '',
                category: prod.category || 'Bánh nướng',
                description: prod.description || '',
                filling: prod.filling || '',
                price: prod.price || '',
                imageUrl: prod.imageUrl || '',
                stock_status: prod.stock_status || 'stock'
            });
        } else {
            setEditingProduct(null);
            setProductForm({
                name: '',
                category: 'Bánh nướng',
                description: '',
                filling: '',
                price: '',
                imageUrl: '',
                stock_status: 'stock'
            });
        }
        setShowProductModal(true);
    };

    const handleSaveProduct = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...productForm,
                price: Number(productForm.price)
            };

            if (editingProduct) {
                await axios.put(`${API_BASE}/product/updateProduct/${editingProduct.id}`, payload);
                Swal.fire({ icon: 'success', title: 'Đã cập nhật món bánh!' });
            } else {
                await axios.post(`${API_BASE}/product/createProduct`, payload);
                Swal.fire({ icon: 'success', title: 'Đã thêm món bánh mới!' });
            }
            setShowProductModal(false);
            setPreviewUrl('');
            const pRes = await axios.get(`${API_BASE}/product/getProduct`);
            if (pRes.data?.data) setProducts(pRes.data.data);
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Không thể lưu sản phẩm!' });
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({ icon: 'warning', title: 'File quá lớn', text: 'Vui lòng chọn ảnh dưới 5MB.' });
            return;
        }

        // Tạo preview lập tức từ file vừa chọn trong máy
        try {
            const localPreview = URL.createObjectURL(file);
            setPreviewUrl(localPreview);
        } catch (err) {
            console.error(err);
        }

        const formData = new FormData();
        formData.append('image', file);

        setUploadingImg(true);
        try {
            let res;
            try {
                res = await axios.post(`${API_BASE}/upload`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } catch (err1) {
                res = await axios.post(`${API_BASE}/product/image/upload`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            const url = res.data?.imageUrl || (res.data?.fileName ? `/imgs/${res.data.fileName}` : '');
            if (url) {
                setProductForm(prev => ({ ...prev, imageUrl: url }));
                const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
                Toast.fire({ icon: 'success', title: 'Tải ảnh lên thành công!' });
            } else {
                throw new Error('Không nhận được đường dẫn ảnh');
            }
        } catch (err) {
            console.error('Lỗi upload ảnh:', err);
            Swal.fire({ icon: 'error', title: 'Lỗi tải ảnh', text: 'Không thể tải ảnh từ máy lên. Vui lòng thử lại!' });
        } finally {
            setUploadingImg(false);
            e.target.value = '';
        }
    };

    const handleDeleteProduct = (prodId) => {
        Swal.fire({
            title: 'Xoá sản phẩm này?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#D32F2F',
            confirmButtonText: 'Đồng ý xoá'
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axios.delete(`${API_BASE}/product/deleteProduct/${prodId}`);
                    setProducts(prev => prev.filter(p => p.id !== prodId));
                    Swal.fire({ icon: 'success', title: 'Đã xoá' });
                } catch (e) {
                    Swal.fire({ icon: 'error', title: 'Lỗi xoá sản phẩm' });
                }
            }
        });
    };

    // Calculate stats
    // Shared with the backend via billPayment.js; an order still owing a flavour-change
    // difference ('partial') counts as unpaid so it shows up in the unpaid filter.
    const isBillPaid = (b) => isFullyPaid(b);

    // Helper: Identify delivered orders (distinguishing from "Đang giao hàng")
    const isBillDelivered = (b) => {
        if (!b) return false;
        const s = (b.status || '').toLowerCase().trim();
        return s === 'đã giao hàng' || s === 'đã giao' || s.includes('đã giao') || s.includes('da giao') || s === 'delivered' || s === 'completed';
    };

    // Helper: Identify cancelled orders
    const isBillCancelled = (b) => {
        if (!b) return false;
        const s = (b.status || '').toLowerCase().trim();
        return s.includes('hủy') || s.includes('cancel') || b.paymentStatus === 'cancelled';
    };

    // Master filter for Batch Production & Recipe Calculation:
    // By default ('all_active'): Exclude cancelled AND delivered orders so workshop only bakes for active undelivered orders!
    const filterBillByBatchStatus = useCallback((bill, filterType = 'all_active') => {
        if (isBillCancelled(bill)) return false;
        const delivered = isBillDelivered(bill);
        const paid = isBillPaid(bill);

        switch (filterType) {
            case 'paid_only':
                return !delivered && paid;
            case 'pending_only':
                return !delivered && !paid;
            case 'delivered_only':
                return delivered;
            case 'all_including_delivered':
                return true;
            case 'all_active':
            default:
                return !delivered;
        }
    }, []);
    const paidBills = bills.filter(isBillPaid);
    const pendingBills = bills.filter(b => {
        const s = (b.status || '').toLowerCase();
        return !isBillPaid(b) && !s.includes('hủy') && b.paymentStatus !== 'cancelled';
    });
    // Money actually kept: what was received, capped at the order total (an overpayment is owed back).
    // Identical to summing paid totals until a flavour change moves a price.
    const totalRevenue = bills.reduce((sum, b) => sum + Math.min(paidAmountOf(b), money(b.totalAmount)), 0);

    // Group bills by delivery date
    const groupedByDate = {};
    bills.forEach(bill => {
        // Filter by status if not all
        if (statusFilter !== 'all') {
            const s = (bill.status || '').toLowerCase();
            const paid = isBillPaid(bill);
            if (statusFilter === 'unpaid' && paid) return;
            if (statusFilter === 'paid' && !paid) return;
            if (statusFilter === 'make' && !s.includes('làm') && !s.includes('chuẩn bị')) return;
            if (statusFilter === 'done' && !s.includes('giao')) return;
        }

        const d = bill.delivery_date ? bill.delivery_date.split('T')[0] : 'Chưa định ngày';
        if (!groupedByDate[d]) groupedByDate[d] = [];
        groupedByDate[d].push(bill);
    });

    const formatBillItems = (items) => {
        try {
            const arr = typeof items === 'string' ? JSON.parse(items) : items;
            if (!Array.isArray(arr)) return String(items);
            return arr.map(it => {
                const qty = it.qty || 1;
                const subCakes = Array.isArray(it.items) ? it.items : [];
                const isBox = (it.isBox || (it.name && it.name.startsWith('Hộp')) || (it.capacity && it.capacity > 1)) && !it.isSingle;

                if (isBox && subCakes.length > 0) {
                    const subText = subCakes.map((c, i) => `[Ô ${i + 1}] ${c.name}`).join(' · ');
                    return `${qty}× ${it.name} (${subText})`;
                }
                if (subCakes.length === 1 && !isBox) {
                    const s = subCakes[0];
                    const eggStr = s.hasEgg ? '1 trứng' : 'không trứng';
                    return `${qty}× ${it.name} (${s.weight || '150g'}, ${eggStr})`;
                }
                if (it.itemsSummary) {
                    return `${qty}× ${it.name} (${it.itemsSummary})`;
                }
                return `${qty}× ${it.name}`;
            }).join(' | ');
        } catch (e) {
            return String(items);
        }
    };
    const parseItemsSummary = formatBillItems;

    const renderBillItemsDetail = (itemsRaw) => {
        let items = [];
        try {
            items = typeof itemsRaw === 'string' ? JSON.parse(itemsRaw) : itemsRaw;
        } catch (e) {
            return <div>{String(itemsRaw)}</div>;
        }
        if (!Array.isArray(items) || items.length === 0) {
            return <div style={{ color: '#8B7A6D' }}>Chưa có thông tin bánh</div>;
        }

        return (
            <div className="bill-detail-items-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {items.map((it, idx) => {
                    const subCakes = Array.isArray(it.items) ? it.items : [];
                    const isBox = Boolean((it.isBox || (it.name && it.name.startsWith('Hộp')) || (it.capacity && it.capacity > 1)) && !it.isSingle);
                    const isSingle = !isBox;
                    const singleSub = subCakes.length > 0 ? subCakes[0] : it;
                    const cakeWeight = singleSub.weight || it.weight || '150g';
                    let cakeHasEgg = false;
                    if (singleSub.hasEgg !== undefined) {
                        cakeHasEgg = Boolean(singleSub.hasEgg);
                    } else if (it.hasEgg !== undefined) {
                        cakeHasEgg = Boolean(it.hasEgg);
                    } else if (singleSub.egg !== undefined) {
                        cakeHasEgg = Boolean(String(singleSub.egg).includes('1') && !String(singleSub.egg).toLowerCase().includes('không'));
                    } else if (it.egg !== undefined) {
                        cakeHasEgg = Boolean(String(it.egg).includes('1') && !String(it.egg).toLowerCase().includes('không'));
                    } else if (it.itemsSummary) {
                        const sLower = it.itemsSummary.toLowerCase();
                        cakeHasEgg = sLower.includes('1 trứng') && !sLower.includes('không trứng');
                    }

                    return (
                        <div
                            key={it.id || idx}
                            style={{
                                background: '#FFFFFF',
                                border: '1px solid #E6D8BF',
                                borderRadius: '8px',
                                padding: '12px 14px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                            }}
                        >
                            {/* Header của sản phẩm / Hộp */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: isBox && subCakes.length > 0 ? '10px' : '0',
                                    borderBottom: isBox && subCakes.length > 0 ? '1px dashed #EADDC7' : 'none',
                                    paddingBottom: isBox && subCakes.length > 0 ? '8px' : '0'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <img
                                        src={it.boxImage || it.imageUrl || it.img || (isBox ? '/imgs/hop-4-banh.webp' : '/imgs/nuong-thap-cam.jpg')}
                                        alt={it.name}
                                        style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #DCC9A4' }}
                                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = isBox ? '/imgs/hop-4-banh.webp' : '/imgs/nuong-thap-cam.jpg'; }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: '14.5px', color: '#142C54' }}>
                                            {it.qty ? `${it.qty}× ` : '1× '}{it.name}
                                        </div>
                                        <div style={{ fontSize: '11.5px', color: '#786657' }}>
                                            {isBox ? (
                                                <span><i className="fa-solid fa-gift" style={{ color: '#B8862F', marginRight: '4px' }}></i> Hộp quà cao cấp {it.capacity ? `(${it.capacity} bánh)` : ''} · Kèm túi xách</span>
                                            ) : (
                                                <span><i className="fa-solid fa-box-open" style={{ color: '#8B7A6D', marginRight: '4px' }}></i> Đóng túi giữ ẩm · Không kèm hộp quà</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ fontWeight: 700, fontSize: '14px', color: '#241A16' }}>
                                    {formatCurrency(it.total || (it.price * (it.qty || 1)))}
                                </div>
                            </div>

                            {/* Danh sách vị trí từng bánh trong hộp (CHỈ HIỆN KHI LÀ HỘP QUÀ) */}
                            {isBox && subCakes.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#8B7A6D', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <i className="fa-solid fa-table-cells-large" style={{ marginRight: '5px' }}></i> Vị trí từng loại bánh trong hộp:
                                    </div>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: '8px'
                                        }}
                                    >
                                        {subCakes.map((c, cIdx) => (
                                            <div
                                                key={c.uid || cIdx}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    background: '#FAF6EF',
                                                    border: '1px solid #EADDC7',
                                                    borderRadius: '6px',
                                                    padding: '7px 9px'
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: '22px',
                                                        height: '22px',
                                                        borderRadius: '50%',
                                                        background: '#142C54',
                                                        color: '#FFFFFF',
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        flexShrink: 0
                                                    }}
                                                    title={`Vị trí ${cIdx + 1}`}
                                                >
                                                    {cIdx + 1}
                                                </span>
                                                <img
                                                    src={c.img || getProductImage(c.name)}
                                                    alt={c.name}
                                                    style={{ width: '34px', height: '34px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #E2D5C1', flexShrink: 0 }}
                                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/imgs/nuong-thap-cam.jpg'; }}
                                                />
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    <div style={{ fontWeight: 600, fontSize: '12px', color: '#241A16', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.name}>
                                                        {c.name}
                                                    </div>
                                                    <div style={{ fontSize: '10.5px', color: '#786657', marginTop: '2px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                        <span style={{ background: '#ECE4D4', padding: '1px 5px', borderRadius: '3px', fontSize: '9.5px', fontWeight: 600 }}>
                                                            {c.weight || '150g'}
                                                        </span>
                                                        <span style={{ background: c.hasEgg ? '#FEE2E2' : '#F1F5F9', color: c.hasEgg ? '#B91C1C' : '#475569', padding: '1px 5px', borderRadius: '3px', fontSize: '9.5px', fontWeight: c.hasEgg ? 600 : 500 }}>
                                                            {c.hasEgg ? '1 Trứng' : 'Không trứng'}
                                                        </span>
                                                        {c.kind && (
                                                            <span style={{ color: '#8B7A6D', fontSize: '9.5px' }}>· {c.kind}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Fallback nếu có itemsSummary nhưng không có mảng items (cho hộp) */}
                            {isBox && subCakes.length === 0 && it.itemsSummary && (
                                <div style={{ marginTop: '8px', fontSize: '12px', color: '#475569', background: '#FAF6EF', padding: '8px 10px', borderRadius: '4px' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '4px', color: '#142C54' }}>Vị trí các bánh trong hộp:</div>
                                    {it.itemsSummary.split('·').map((part, pIdx) => (
                                        <div key={pIdx} style={{ padding: '2px 0' }}>
                                            <strong style={{ color: '#B8862F' }}>Vị trí {pIdx + 1}:</strong> {part.trim()}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Trường hợp bánh lẻ (hiển thị rõ quy cách khối lượng và trứng muối chính xác) */}
                            {isSingle && (
                                <div style={{ marginTop: '8px', fontSize: '12px', color: '#5C4A3F', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ color: '#786657' }}>Quy cách:</span>
                                    <span style={{ background: '#ECE4D4', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, color: '#142C54' }}>
                                        {cakeWeight}
                                    </span>
                                    <span style={{ background: cakeHasEgg ? '#FEE2E2' : '#F1F5F9', color: cakeHasEgg ? '#B91C1C' : '#475569', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: cakeHasEgg ? 700 : 500 }}>
                                        {cakeHasEgg ? '1 Trứng muối' : 'Không trứng'}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Helper to check if a cake has no filling
    const isNoFillingCake = (name, filling) => {
        const text = `${name || ''} ${filling || ''}`.toLowerCase();
        return text.includes('không nhân') || text.includes('khong nhan') || text.includes('thuần vỏ') || text.includes('thuan vo');
    };

    // Helper to extract filling from cake name
    const extractFilling = (name) => {
        if (!name) return 'Khác';
        const n = name.toLowerCase();
        if (n.includes('không nhân') || n.includes('khong nhan') || n.includes('thuần vỏ')) return 'Bánh dẻo không nhân';
        if (n.includes('thập cẩm')) return 'Thập cẩm';
        if (n.includes('đậu xanh')) return 'Đậu xanh vani';
        if (n.includes('cốm dừa') || n.includes('cốm')) return 'Cốm dừa';
        if (n.includes('trà xanh')) return 'Trà xanh';
        if (n.includes('trứng muối') || n.includes('kem trứng')) return 'Kem trứng muối';
        return name;
    };

    const getProductImage = (name) => {
        const found = products.find(p => (p.name || '').toLowerCase() === (name || '').toLowerCase());
        if (found && found.imageUrl) return found.imageUrl;
        const n = (name || '').toLowerCase();
        if (n.includes('nuong-thap-cam') || n.includes('nướng thập cẩm')) return '/imgs/nuong-thap-cam.jpg';
        if (n.includes('dau-xanh') || n.includes('đậu xanh')) return '/imgs/nuong-dau-xanh-vani.jpeg';
        if (n.includes('nuong-com-dua') || n.includes('nướng cốm')) return '/imgs/nuong-com-dua.webp';
        if (n.includes('nuong-tra-xanh') || n.includes('nướng trà')) return '/imgs/nuong-tra-xanh.jpeg';
        if (n.includes('deo-thap-cam') || n.includes('dẻo thập cẩm')) return '/imgs/deo-thap-cam.jpg';
        if (n.includes('deo-dau-xanh') || n.includes('dẻo đậu xanh')) return '/imgs/deo-dau-xanh-vani.webp';
        if (n.includes('deo-tra-xanh') || n.includes('dẻo trà')) return '/imgs/deo-tra-xanh.png';
        if (n.includes('deo-com-dua') || n.includes('dẻo cốm')) return '/imgs/deo-com-dua.jpg';
        if (n.includes('kem trứng') || n.includes('deo-kem-trung')) return '/imgs/deo-kem-trung-muoi.jpeg';
        return '/imgs/nuong-thap-cam.jpg';
    };

    // Filtered bills specifically for batch view and recipe calculator based on batchStatusFilter
    const batchEligibleBills = useMemo(() => {
        return bills.filter(b => filterBillByBatchStatus(b, batchStatusFilter));
    }, [bills, batchStatusFilter, filterBillByBatchStatus]);

    // Dates that have eligible bills to bake, plus currently selected date
    const batchAvailableDates = useMemo(() => {
        const dates = Array.from(
            new Set(
                batchEligibleBills
                    .map(b => b.delivery_date ? b.delivery_date.split('T')[0] : null)
                    .filter(Boolean)
            )
        ).sort();
        if (batchDateFilter !== 'all' && batchDateFilter !== 'today' && batchDateFilter !== 'tomorrow' && !dates.includes(batchDateFilter)) {
            dates.push(batchDateFilter);
            dates.sort();
        }
        return dates;
    }, [batchEligibleBills, batchDateFilter]);

    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const tomorrow = new Date(now.getTime() + 86400000);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    // Helper to count cakes in a bill
    const countCakesInBill = (bill) => {
        if (!bill) return 0;
        let items = [];
        try {
            items = typeof bill.items === 'string' ? JSON.parse(bill.items) : bill.items;
        } catch (e) {
            items = [];
        }
        if (!Array.isArray(items)) return 0;
        let count = 0;
        items.forEach(it => {
            const qty = Number(it.qty) || Number(it.quantity) || 1;
            if (it.isBox || (it.name && it.name.startsWith('Hộp'))) {
                const sub = Array.isArray(it.items) ? it.items : [];
                if (sub.length > 0) {
                    count += sub.length * qty;
                } else {
                    const boxMatch = (it.name || '').match(/(\d+)\s*bánh/i);
                    count += (boxMatch ? Number(boxMatch[1]) : 4) * qty;
                }
            } else {
                count += qty;
            }
        });
        return count;
    };

    // Calculate Batch Production Summary (Gom theo mẻ - Tách biệt 150g và 180g + Gom vị nhân)
    const batchSummary = (() => {
        let totalCakes = 0;
        let totalEggs = 0;
        const packaging = {};
        const matchedBills = [];

        const cakeMap150 = {};
        const cakeMap180 = {};
        const fillingMap150 = {};
        const fillingMap180 = {};
        const allFillingMap = {};

        let nuongNormal150 = 0, nuongNoFill150 = 0;
        let deoNormal150 = 0, deoNoFill150 = 0;
        let egg150 = 0;

        let nuongNormal180 = 0, nuongNoFill180 = 0;
        let deoNormal180 = 0, deoNoFill180 = 0;
        let egg180 = 0;

        let rawTotalNuong = 0, rawTotalDeo = 0;

        // Candidate bills matching date, slot, status (for Order dropdown)
        const candidateBillsForBatch = bills.filter(bill => {
            if (!filterBillByBatchStatus(bill, batchStatusFilter)) return false;

            const billDate = bill.delivery_date ? bill.delivery_date.split('T')[0] : '';
            if (batchDateFilter === 'today' && billDate !== todayStr) return false;
            if (batchDateFilter === 'tomorrow' && billDate !== tomorrowStr) return false;
            if (batchDateFilter !== 'all' && batchDateFilter !== 'today' && batchDateFilter !== 'tomorrow' && billDate !== batchDateFilter) return false;

            if (batchSlotFilter !== 'all' && bill.deliveryTimeSlot !== batchSlotFilter) return false;

            return true;
        });

        bills.forEach(bill => {
            if (!filterBillByBatchStatus(bill, batchStatusFilter)) return;

            const billDate = bill.delivery_date ? bill.delivery_date.split('T')[0] : '';
            if (batchDateFilter === 'today' && billDate !== todayStr) return;
            if (batchDateFilter === 'tomorrow' && billDate !== tomorrowStr) return;
            if (batchDateFilter !== 'all' && batchDateFilter !== 'today' && batchDateFilter !== 'tomorrow' && billDate !== batchDateFilter) return;

            if (batchSlotFilter !== 'all' && bill.deliveryTimeSlot !== batchSlotFilter) return;

            // Filter by specific order if chosen
            if (batchOrderFilter !== 'all' && bill.orderCode !== batchOrderFilter && String(bill.id) !== String(batchOrderFilter)) return;

            matchedBills.push(bill);

            let items = [];
            try {
                items = typeof bill.items === 'string' ? JSON.parse(bill.items) : bill.items;
            } catch (e) {
                items = [];
            }
            if (!Array.isArray(items)) return;

            items.forEach(it => {
                const qty = Number(it.qty) || Number(it.quantity) || 1;
                const isBox = Boolean((it.isBox || (it.name && it.name.startsWith('Hộp')) || (it.capacity && it.capacity > 1)) && !it.isSingle);
                if (isBox) {
                    packaging[it.name] = (packaging[it.name] || 0) + qty;
                }

                // Subcakes array present in gift boxes AND individual cakes placed via GiftBoxPage
                const hasSubArray = Array.isArray(it.items) && it.items.length > 0;
                const cakeList = hasSubArray ? it.items : [it];

                cakeList.forEach(sub => {
                    const rawName = (sub.name || it.name || '').replace(/^Bánh lẻ:\s*/i, '').trim();
                    const subName = rawName || 'Bánh Trung Thu';
                    const isDeo = (sub.category && sub.category.toLowerCase().includes('dẻo')) ||
                                  (sub.kind && sub.kind.toLowerCase().includes('dẻo')) ||
                                  (it.category && it.category.toLowerCase().includes('dẻo')) ||
                                  subName.toLowerCase().includes('dẻo');
                    const category = isDeo ? 'Bánh dẻo' : 'Bánh nướng';
                    const filling = sub.filling || it.filling || extractFilling(subName);
                    const weightStr = (sub.weight || it.weight || '150g') + '';
                    const weight = weightStr.toLowerCase().includes('180') ? '180g' : '150g';

                    let hasEgg = false;
                    if (hasSubArray) {
                        if (sub.hasEgg !== undefined) {
                            hasEgg = Boolean(sub.hasEgg);
                        } else if (sub.egg !== undefined) {
                            hasEgg = Boolean(String(sub.egg).includes('1') && !String(sub.egg).toLowerCase().includes('không'));
                        }
                    } else {
                        if (it.hasEgg !== undefined) {
                            hasEgg = Boolean(it.hasEgg);
                        } else if (it.egg !== undefined) {
                            hasEgg = Boolean(String(it.egg).includes('1') && !String(it.egg).toLowerCase().includes('không'));
                        } else if (it.eggOptions !== undefined) {
                            hasEgg = Boolean(String(it.eggOptions).includes('1') && !String(it.eggOptions).toLowerCase().includes('không'));
                        } else if (it.itemsSummary) {
                            const sLower = it.itemsSummary.toLowerCase();
                            hasEgg = sLower.includes('1 trứng') && !sLower.includes('không trứng');
                        }
                    }
                    const isNoFill = isNoFillingCake(subName, filling);

                    const targetCakeMap = weight === '180g' ? cakeMap180 : cakeMap150;
                    const targetFillingMap = weight === '180g' ? fillingMap180 : fillingMap150;

                    const key = `${subName}__${weight}`;
                    if (!targetCakeMap[key]) {
                        targetCakeMap[key] = {
                            key,
                            name: subName,
                            category,
                            filling,
                            weight,
                            imageUrl: sub.imageUrl || sub.image || sub.img || it.imageUrl || it.image || it.img || getProductImage(subName),
                            isNoFilling: isNoFill,
                            qtyWithEgg: 0,
                            qtyNoEgg: 0,
                            totalQty: 0,
                            orders: []
                        };
                    }

                    if (hasEgg) {
                        targetCakeMap[key].qtyWithEgg += qty;
                        totalEggs += qty;
                        if (weight === '180g') egg180 += qty; else egg150 += qty;
                    } else {
                        targetCakeMap[key].qtyNoEgg += qty;
                    }
                    targetCakeMap[key].totalQty += qty;
                    totalCakes += qty;

                    if (weight === '180g') {
                        if (isDeo) {
                            rawTotalDeo += qty;
                            if (isNoFill) deoNoFill180 += qty; else deoNormal180 += qty;
                        } else {
                            rawTotalNuong += qty;
                            if (isNoFill) nuongNoFill180 += qty; else nuongNormal180 += qty;
                        }
                    } else {
                        if (isDeo) {
                            rawTotalDeo += qty;
                            if (isNoFill) deoNoFill150 += qty; else deoNormal150 += qty;
                        } else {
                            rawTotalNuong += qty;
                            if (isNoFill) nuongNoFill150 += qty; else nuongNormal150 += qty;
                        }
                    }

                    targetCakeMap[key].orders.push({
                        orderCode: bill.orderCode,
                        customerName: bill.customerName,
                        customerPhone: bill.customerPhone,
                        deliveryDate: bill.delivery_date,
                        deliverySlot: bill.deliveryTimeSlot,
                        qty,
                        weight,
                        hasEgg,
                        inBox: isBox ? it.name : null
                    });

                    if (!targetFillingMap[filling]) {
                        targetFillingMap[filling] = { 
                            filling, 
                            nuongQty: 0, 
                            deoQty: 0, 
                            totalQty: 0, 
                            eggCount: 0,
                            isNoFilling: isNoFill
                        };
                    }
                    targetFillingMap[filling].totalQty += qty;
                    if (hasEgg) targetFillingMap[filling].eggCount += qty;
                    if (isDeo) targetFillingMap[filling].deoQty += qty; else targetFillingMap[filling].nuongQty += qty;

                    // Combined filling across all weights
                    if (!allFillingMap[filling]) {
                        allFillingMap[filling] = {
                            filling,
                            totalQty: 0,
                            qty150: 0,
                            qty180: 0,
                            nuong150: 0,
                            deo150: 0,
                            nuong180: 0,
                            deo180: 0,
                            eggTotal: 0,
                            egg150: 0,
                            egg180: 0,
                            isNoFilling: isNoFill,
                            orders: []
                        };
                    }
                    allFillingMap[filling].totalQty += qty;
                    if (weight === '180g') {
                        allFillingMap[filling].qty180 += qty;
                        if (isDeo) allFillingMap[filling].deo180 += qty; else allFillingMap[filling].nuong180 += qty;
                        if (hasEgg) {
                            allFillingMap[filling].eggTotal += qty;
                            allFillingMap[filling].egg180 += qty;
                        }
                    } else {
                        allFillingMap[filling].qty150 += qty;
                        if (isDeo) allFillingMap[filling].deo150 += qty; else allFillingMap[filling].nuong150 += qty;
                        if (hasEgg) {
                            allFillingMap[filling].eggTotal += qty;
                            allFillingMap[filling].egg150 += qty;
                        }
                    }
                    allFillingMap[filling].orders.push({
                        orderCode: bill.orderCode,
                        customerName: bill.customerName,
                        customerPhone: bill.customerPhone,
                        qty,
                        weight,
                        category,
                        hasEgg,
                        inBox: isBox ? it.name : null
                    });
                });
            });
        });

        // Combined fillings calculation (gộp cả 150g và 180g)
        const consolidatedFillings = Object.values(allFillingMap).map(f => {
            const isNF = f.isNoFilling || isNoFillingCake('', f.filling);
            // Bánh không nhân thì 0g nhân!
            const fillingKg150 = isNF ? 0 : Number(((f.nuong150 * 95 + f.deo150 * 75) / 1000).toFixed(2));
            const fillingKg180 = isNF ? 0 : Number(((f.nuong180 * 115 + f.deo180 * 90) / 1000).toFixed(2));
            const totalFillingKg = isNF ? 0 : Number((fillingKg150 + fillingKg180).toFixed(2));
            // Bột vỏ: Bánh không nhân dùng 150g (hoặc 180g) vỏ!
            const doughKg150 = isNF 
                ? Number(((f.qty150 * 150) / 1000).toFixed(2)) 
                : Number(((f.nuong150 * 55 + f.deo150 * 75) / 1000).toFixed(2));
            const doughKg180 = isNF 
                ? Number(((f.qty180 * 180) / 1000).toFixed(2)) 
                : Number(((f.nuong180 * 65 + f.deo180 * 90) / 1000).toFixed(2));
            const totalDoughKg = Number((doughKg150 + doughKg180).toFixed(2));

            return {
                ...f,
                isNoFilling: isNF,
                fillingKg150,
                fillingKg180,
                totalFillingKg,
                doughKg150,
                doughKg180,
                totalDoughKg
            };
        }).sort((a, b) => b.totalQty - a.totalQty);

        const availableFillings = consolidatedFillings.map(f => ({
            filling: f.filling,
            totalQty: f.totalQty,
            isNoFilling: f.isNoFilling
        }));

        // Filling filter handling
        const isFilterFilling = batchFillingFilter !== 'all';
        const isCurNF = isNoFillingCake('', batchFillingFilter);

        const rawCakes150 = Object.values(cakeMap150).sort((a, b) => b.totalQty - a.totalQty);
        const rawFillings150 = Object.values(fillingMap150).map(f => {
            const isNF = f.isNoFilling || isNoFillingCake('', f.filling);
            const fillingKg = isNF ? 0 : Number(((f.nuongQty * 95 + f.deoQty * 75) / 1000).toFixed(2));
            const doughKg = isNF 
                ? Number(((f.nuongQty * 150 + f.deoQty * 150) / 1000).toFixed(2))
                : Number(((f.nuongQty * 55 + f.deoQty * 75) / 1000).toFixed(2));
            return {
                ...f,
                isNoFilling: isNF,
                fillingKg,
                doughKg
            };
        }).sort((a, b) => b.totalQty - a.totalQty);

        const rawCakes180 = Object.values(cakeMap180).sort((a, b) => b.totalQty - a.totalQty);
        const rawFillings180 = Object.values(fillingMap180).map(f => {
            const isNF = f.isNoFilling || isNoFillingCake('', f.filling);
            const fillingKg = isNF ? 0 : Number(((f.nuongQty * 115 + f.deoQty * 90) / 1000).toFixed(2));
            const doughKg = isNF 
                ? Number(((f.nuongQty * 180 + f.deoQty * 180) / 1000).toFixed(2))
                : Number(((f.nuongQty * 65 + f.deoQty * 90) / 1000).toFixed(2));
            return {
                ...f,
                isNoFilling: isNF,
                fillingKg,
                doughKg
            };
        }).sort((a, b) => b.totalQty - a.totalQty);

        const cakes150 = isFilterFilling ? rawCakes150.filter(c => c.filling === batchFillingFilter) : rawCakes150;
        const fillings150 = isFilterFilling ? rawFillings150.filter(f => f.filling === batchFillingFilter) : rawFillings150;
        const cakes180 = isFilterFilling ? rawCakes180.filter(c => c.filling === batchFillingFilter) : rawCakes180;
        const fillings180 = isFilterFilling ? rawFillings180.filter(f => f.filling === batchFillingFilter) : rawFillings180;
        const activeConsolidatedFillings = isFilterFilling ? consolidatedFillings.filter(f => f.filling === batchFillingFilter) : consolidatedFillings;

        // Current active 150g metrics (filtered or full)
        const nuong150 = nuongNormal150 + nuongNoFill150;
        const deo150 = deoNormal150 + deoNoFill150;
        const nuong180 = nuongNormal180 + nuongNoFill180;
        const deo180 = deoNormal180 + deoNoFill180;

        const f150 = fillingMap150[batchFillingFilter];
        const curNuong150 = isFilterFilling ? (f150 ? f150.nuongQty : 0) : nuong150;
        const curDeo150 = isFilterFilling ? (f150 ? f150.deoQty : 0) : deo150;
        const curEgg150 = isFilterFilling ? (f150 ? f150.eggCount : 0) : egg150;
        const curTotal150 = curNuong150 + curDeo150;

        // Bột vỏ 150g: Bánh không nhân cần 150g vỏ!
        const doughNuongKg150 = isFilterFilling
            ? (isCurNF ? Number(((curNuong150 * 150) / 1000).toFixed(2)) : Number(((curNuong150 * 55) / 1000).toFixed(2)))
            : Number(((nuongNormal150 * 55 + nuongNoFill150 * 150) / 1000).toFixed(2));
        const doughDeoKg150 = isFilterFilling
            ? (isCurNF ? Number(((curDeo150 * 150) / 1000).toFixed(2)) : Number(((curDeo150 * 75) / 1000).toFixed(2)))
            : Number(((deoNormal150 * 75 + deoNoFill150 * 150) / 1000).toFixed(2));
        const doughTotalKg150 = Number((doughNuongKg150 + doughDeoKg150).toFixed(2));
        // Tổng nhân: Bánh không nhân thì 0g nhân!
        const fillingTotalKg150 = isFilterFilling
            ? (isCurNF ? 0 : Number(((curNuong150 * 95 + curDeo150 * 75) / 1000).toFixed(2)))
            : Number(((nuongNormal150 * 95 + deoNormal150 * 75) / 1000).toFixed(2));

        // Current active 180g metrics (filtered or full)
        const f180 = fillingMap180[batchFillingFilter];
        const curNuong180 = isFilterFilling ? (f180 ? f180.nuongQty : 0) : nuong180;
        const curDeo180 = isFilterFilling ? (f180 ? f180.deoQty : 0) : deo180;
        const curEgg180 = isFilterFilling ? (f180 ? f180.eggCount : 0) : egg180;
        const curTotal180 = curNuong180 + curDeo180;

        // Bột vỏ 180g: Bánh không nhân cần 180g vỏ!
        const doughNuongKg180 = isFilterFilling
            ? (isCurNF ? Number(((curNuong180 * 180) / 1000).toFixed(2)) : Number(((curNuong180 * 65) / 1000).toFixed(2)))
            : Number(((nuongNormal180 * 65 + nuongNoFill180 * 180) / 1000).toFixed(2));
        const doughDeoKg180 = isFilterFilling
            ? (isCurNF ? Number(((curDeo180 * 180) / 1000).toFixed(2)) : Number(((curDeo180 * 90) / 1000).toFixed(2)))
            : Number(((deoNormal180 * 90 + deoNoFill180 * 180) / 1000).toFixed(2));
        const doughTotalKg180 = Number((doughNuongKg180 + doughDeoKg180).toFixed(2));
        // Tổng nhân 180g: Bánh không nhân thì 0g nhân!
        const fillingTotalKg180 = isFilterFilling
            ? (isCurNF ? 0 : Number(((curNuong180 * 115 + curDeo180 * 90) / 1000).toFixed(2)))
            : Number(((nuongNormal180 * 115 + deoNormal180 * 90) / 1000).toFixed(2));

        const displayTotalCakes = curTotal150 + curTotal180;
        const displayTotalNuong = curNuong150 + curNuong180;
        const displayTotalDeo = curDeo150 + curDeo180;
        const displayTotalEggs = curEgg150 + curEgg180;

        return {
            totalBills: matchedBills.length,
            matchedBills,
            candidateBillsForBatch,
            totalCakes: displayTotalCakes,
            totalNuong: displayTotalNuong,
            totalDeo: displayTotalDeo,
            totalEggs: displayTotalEggs,
            rawTotalCakes: totalCakes,
            rawTotalNuong,
            rawTotalDeo,
            rawTotalEggs: totalEggs,
            packaging,
            batch150: {
                weight: '150g',
                title: 'Mẻ Bánh 150g (Khuôn tiêu chuẩn)',
                formula: 'Vỏ nướng ~55g · Vỏ dẻo ~75g | Nhân nướng ~95g · Nhân dẻo ~75g',
                totalCakes: curTotal150,
                totalNuong: curNuong150,
                totalDeo: curDeo150,
                totalEggs: curEgg150,
                doughNuongKg: doughNuongKg150,
                doughDeoKg: doughDeoKg150,
                doughTotalKg: doughTotalKg150,
                fillingTotalKg: fillingTotalKg150,
                cakes: cakes150,
                fillings: fillings150
            },
            batch180: {
                weight: '180g',
                title: 'Mẻ Bánh 180g (Khuôn bánh đại)',
                formula: 'Vỏ nướng ~65g · Vỏ dẻo ~90g | Nhân nướng ~115g · Nhân dẻo ~90g',
                totalCakes: curTotal180,
                totalNuong: curNuong180,
                totalDeo: curDeo180,
                totalEggs: curEgg180,
                doughNuongKg: doughNuongKg180,
                doughDeoKg: doughDeoKg180,
                doughTotalKg: doughTotalKg180,
                fillingTotalKg: fillingTotalKg180,
                cakes: cakes180,
                fillings: fillings180
            },
            allCakes: [
                ...cakes150,
                ...cakes180
            ].sort((a, b) => b.totalQty - a.totalQty),
            consolidatedFillings: activeConsolidatedFillings,
            allConsolidatedFillings: consolidatedFillings,
            availableFillings
        };
    })();

    const handlePrintBatch = () => {
        window.print();
    };

    const handleCopyBatchSummary = () => {
        let text = `📋 KẾ HOẠCH LÀM BÁNH THEO MẺ (${batchDateFilter === 'all' ? 'Toàn bộ đơn' : batchDateFilter})\n`;
        if (batchOrderFilter !== 'all') {
            text += `📦 Đang lọc riêng theo Đơn hàng: #${batchOrderFilter}\n`;
        }
        if (batchFillingFilter !== 'all') {
            text += `🥣 Đang lọc riêng theo Vị nhân: ${batchFillingFilter}\n`;
        }
        text += `🎂 TỔNG BÁNH CẦN LÀM: ${batchSummary.totalCakes} chiếc (Nướng: ${batchSummary.totalNuong}, Dẻo: ${batchSummary.totalDeo}, Trứng muối: ${batchSummary.totalEggs} quả)\n\n`;

        if (batchWeightFilter === 'all' || batchWeightFilter === '150g') {
            text += `==============================\n`;
            text += `🥮 MẺ 150G (KHUÔN TIÊU CHUẨN 150G)\n`;
            text += `Định lượng: Vỏ nướng 55g · Vỏ dẻo 75g | Nhân nướng 95g · Nhân dẻo 75g\n`;
            text += `• Tổng bánh 150g: ${batchSummary.batch150.totalCakes} chiếc (Nướng: ${batchSummary.batch150.totalNuong}, Dẻo: ${batchSummary.batch150.totalDeo})\n`;
            text += `• Bột vỏ Nướng cần nhào: ${batchSummary.batch150.doughNuongKg} kg\n`;
            text += `• Bột vỏ Dẻo cần trộn: ${batchSummary.batch150.doughDeoKg} kg\n`;
            text += `• Tổng nhân cần cân: ${batchSummary.batch150.fillingTotalKg} kg\n`;
            text += `• Lòng đỏ trứng muối: ${batchSummary.batch150.totalEggs} quả\n\n`;
            text += `--- Chi tiết các món bánh 150g ---\n`;
            batchSummary.batch150.cakes.forEach((c, i) => {
                text += `${i + 1}. ${c.name}: ${c.totalQty} cái (Có trứng: ${c.qtyWithEgg}, K.trứng: ${c.qtyNoEgg})\n`;
            });
            text += `--- Chi tiết nhân mẻ 150g ---\n`;
            batchSummary.batch150.fillings.forEach(f => {
                if (f.isNoFilling) {
                    text += `- ${f.filling} (Thuần vỏ): ${f.totalQty} cái (0 kg nhân · Bột vỏ: ${f.doughKg} kg)\n`;
                } else {
                    text += `- Nhân ${f.filling}: ${f.totalQty} cái (~${f.fillingKg} kg nhân, ${f.eggCount} quả trứng)\n`;
                }
            });
            text += `\n`;
        }

        if (batchWeightFilter === 'all' || batchWeightFilter === '180g') {
            text += `==============================\n`;
            text += `👑 MẺ 180G (KHUÔN ĐẠI 180G)\n`;
            text += `Định lượng: Vỏ nướng 65g · Vỏ dẻo 90g | Nhân nướng 115g · Nhân dẻo 90g\n`;
            text += `• Tổng bánh 180g: ${batchSummary.batch180.totalCakes} chiếc (Nướng: ${batchSummary.batch180.totalNuong}, Dẻo: ${batchSummary.batch180.totalDeo})\n`;
            text += `• Bột vỏ Nướng cần nhào: ${batchSummary.batch180.doughNuongKg} kg\n`;
            text += `• Bột vỏ Dẻo cần trộn: ${batchSummary.batch180.doughDeoKg} kg\n`;
            text += `• Tổng nhân cần cân: ${batchSummary.batch180.fillingTotalKg} kg\n`;
            text += `• Lòng đỏ trứng muối: ${batchSummary.batch180.totalEggs} quả\n\n`;
            text += `--- Chi tiết các món bánh 180g ---\n`;
            batchSummary.batch180.cakes.forEach((c, i) => {
                text += `${i + 1}. ${c.name}: ${c.totalQty} cái (Có trứng: ${c.qtyWithEgg}, K.trứng: ${c.qtyNoEgg})\n`;
            });
            text += `--- Chi tiết nhân mẻ 180g ---\n`;
            batchSummary.batch180.fillings.forEach(f => {
                if (f.isNoFilling) {
                    text += `- ${f.filling} (Thuần vỏ): ${f.totalQty} cái (0 kg nhân · Bột vỏ: ${f.doughKg} kg)\n`;
                } else {
                    text += `- Nhân ${f.filling}: ${f.totalQty} cái (~${f.fillingKg} kg nhân, ${f.eggCount} quả trứng)\n`;
                }
            });
            text += `\n`;
        }

        if (Object.keys(batchSummary.packaging).length > 0) {
            text += `--- VỎ HỘP QUÀ CẦN CHUẨN BỊ ---\n`;
            Object.entries(batchSummary.packaging).forEach(([k, v]) => {
                text += `🎁 ${k}: ${v} hộp\n`;
            });
        }

        navigator.clipboard.writeText(text);
        Swal.fire({
            icon: 'success',
            title: 'Đã sao chép danh sách mẻ!',
            text: 'Đã phân tách mẻ 150g và 180g kèm khối lượng vỏ, nhân theo bộ lọc. Bạn có thể dán vào Zalo ngay.',
            timer: 2000,
            showConfirmButton: false
        });
    };

    const toggleCompleteCake = (key) => {
        setCompletedCakes(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Render a separated batch size section (150g or 180g)
    const renderBatchSizeBlock = (bData, badgeClass) => {
        if (!bData || bData.totalCakes === 0) {
            return (
                <div className={`batch-size-section ${badgeClass === 'b150' ? 'size-150' : 'size-180'}`}>
                    <div className="batch-size-header">
                        <div className="batch-size-title-group">
                            <span className={`batch-size-badge ${badgeClass}`}>{bData.weight}</span>
                            <div>
                                <h3 className="batch-size-title">{bData.title}</h3>
                                <p className="batch-size-formula">{bData.formula}</p>
                            </div>
                        </div>
                    </div>
                    <div style={{ padding: '30px 20px', textAlign: 'center', color: '#8F7A69', fontSize: '13.5px' }}>
                        {batchFillingFilter !== 'all' 
                            ? `Không có bánh nào dùng nhân "${batchFillingFilter}" trong mẻ ${bData.weight}.`
                            : `Chưa có đơn hàng nào cho mẻ ${bData.weight} trong bộ lọc hiện tại.`}
                    </div>
                </div>
            );
        }

        const is150 = bData.weight === '150g';

        return (
            <div className={`batch-size-section ${is150 ? 'size-150' : 'size-180'}`}>
                <div className="batch-size-header">
                    <div className="batch-size-title-group">
                        <span className={`batch-size-badge ${badgeClass}`}>{bData.weight}</span>
                        <div>
                            <h3 className="batch-size-title">
                                {bData.title} — <strong style={{ color: is150 ? '#142C54' : '#B8862F' }}>{bData.totalCakes} bánh</strong>
                            </h3>
                            <p className="batch-size-formula">
                                Tỷ lệ khối lượng: <strong>{bData.formula}</strong>
                            </p>
                        </div>
                    </div>
                    <div style={{ fontSize: '12.5px', color: '#6B5A4E', background: '#FAF1DF', padding: '6px 14px', borderRadius: '8px', border: '1px solid #DCC9A4' }}>
                        Khuôn đóng bánh: <strong>Khuôn {bData.weight}</strong>
                    </div>
                </div>

                {/* 4 Ingredient Preparation Metric Strip */}
                <div className="batch-ingredient-strip">
                    <div className="batch-ing-card">
                        <div className="batch-ing-icon nuong">
                            <i className="fa-solid fa-fire-burner"></i>
                        </div>
                        <div className="batch-ing-info">
                            <div className="batch-ing-label">Bột vỏ Nướng ({is150 ? '55g' : '65g'}/cái)</div>
                            <div className="batch-ing-val">{bData.doughNuongKg} <span style={{ fontSize: '15px' }}>kg</span></div>
                            <div className="batch-ing-sub">{bData.totalNuong} bánh nướng cần nhào bột</div>
                        </div>
                    </div>

                    <div className="batch-ing-card">
                        <div className="batch-ing-icon deo">
                            <i className="fa-solid fa-spa"></i>
                        </div>
                        <div className="batch-ing-info">
                            <div className="batch-ing-label">Bột vỏ Dẻo ({is150 ? '75g' : '90g'}/cái)</div>
                            <div className="batch-ing-val">{bData.doughDeoKg} <span style={{ fontSize: '15px' }}>kg</span></div>
                            <div className="batch-ing-sub">{bData.totalDeo} bánh dẻo trộn nếp chín</div>
                        </div>
                    </div>

                    <div className="batch-ing-card">
                        <div className="batch-ing-icon filling">
                            <i className="fa-solid fa-bowl-rice"></i>
                        </div>
                        <div className="batch-ing-info">
                            <div className="batch-ing-label">Tổng nhân ({is150 ? '95g/75g' : '115g/90g'})</div>
                            <div className="batch-ing-val">{bData.fillingTotalKg} <span style={{ fontSize: '15px' }}>kg</span></div>
                            <div className="batch-ing-sub">Tổng khối lượng nhân cần cân viên</div>
                        </div>
                    </div>

                    <div className="batch-ing-card">
                        <div className="batch-ing-icon egg">
                            <i className="fa-solid fa-egg"></i>
                        </div>
                        <div className="batch-ing-info">
                            <div className="batch-ing-label">Trứng muối ({bData.weight})</div>
                            <div className="batch-ing-val">{bData.totalEggs} <span style={{ fontSize: '15px' }}>quả</span></div>
                            <div className="batch-ing-sub">Lòng đỏ nướng/sơ chế sẵn sàng</div>
                        </div>
                    </div>
                </div>

                {/* TABLE OR FILLING CARDS FOR THIS SPECIFIC BATCH */}
                {batchViewMode === 'cakes' ? (
                    <div className="batch-table-wrapper">
                        <table className="batch-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '48px', textAlign: 'center', whiteSpace: 'nowrap' }}>STT</th>
                                    <th style={{ minWidth: '220px' }}>Món bánh ({bData.weight})</th>
                                    <th style={{ width: '130px', minWidth: '120px', whiteSpace: 'nowrap' }}>Dòng bánh</th>
                                    <th style={{ width: '120px', minWidth: '110px', textAlign: 'center', whiteSpace: 'nowrap' }}>Có trứng muối</th>
                                    <th style={{ width: '110px', minWidth: '100px', textAlign: 'center', whiteSpace: 'nowrap' }}>Không trứng</th>
                                    <th style={{ width: '130px', minWidth: '120px', textAlign: 'center', whiteSpace: 'nowrap' }}>TỔNG MẺ {bData.weight}</th>
                                    <th style={{ minWidth: '180px' }}>Đơn hàng cần giao</th>
                                    <th style={{ width: '70px', textAlign: 'center', whiteSpace: 'nowrap' }}>Xong</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bData.cakes.map((cake, idx) => {
                                    const isDone = Boolean(completedCakes[cake.key]);
                                    return (
                                        <tr key={cake.key} className={isDone ? 'completed-row' : ''}>
                                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#8F7A69', whiteSpace: 'nowrap' }}>
                                              {idx + 1}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <img
                                                        src={getImageUrl(cake.imageUrl)}
                                                        alt={cake.name}
                                                        style={{ width: '42px', height: '42px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #E6D9C3' }}
                                                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/imgs/nuong-thap-cam.jpg'; }}
                                                    />
                                                    <div>
                                                        <div style={{ fontWeight: 700, fontSize: '14.5px', color: '#241A16' }}>
                                                            {cake.name}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#8F7A69' }}>
                                                            {cake.isNoFilling ? (
                                                                <span style={{ color: '#28A745', fontWeight: 700 }}>Thuần vỏ dẻo (0g nhân)</span>
                                                            ) : (
                                                                <>Nhân: <strong>{cake.filling}</strong></>
                                                            )} · Khuôn {bData.weight}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <span className={cake.category === 'Bánh nướng' ? 'batch-badge-nuong' : 'batch-badge-deo'}>
                                                    {cake.category}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                {cake.qtyWithEgg > 0 ? (
                                                    <span className="batch-egg-tag">
                                                        <i className="fa-solid fa-egg"></i> {cake.qtyWithEgg} cái
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#A09083', fontSize: '12px' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                {cake.qtyNoEgg > 0 ? (
                                                    <span style={{ fontWeight: 600, color: '#241A16' }}>
                                                        {cake.qtyNoEgg} cái
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#A09083', fontSize: '12px' }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className="batch-qty-pill" style={{ background: is150 ? '#142C54' : '#B8862F' }}>
                                                    {cake.totalQty}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                    {cake.orders.map((ord, oIdx) => (
                                                        <span
                                                            key={oIdx}
                                                            style={{
                                                                fontSize: '11px',
                                                                fontWeight: 600,
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                background: '#F4E9D5',
                                                                color: '#241A16',
                                                                border: '1px solid #DCC9A4'
                                                            }}
                                                            title={`${ord.customerName} (${ord.customerPhone || 'Không SĐT'}) · ${ord.qty} chiếc · Ngày: ${formatDateShort(ord.deliveryDate)} (${ord.deliverySlot})${ord.inBox ? ' · Trong ' + ord.inBox : ''}`}
                                                        >
                                                            #{ord.orderCode} ({ord.qty})
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isDone}
                                                    onChange={() => toggleCompleteCake(cake.key)}
                                                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#1E7E34' }}
                                                    title="Đánh dấu đã nướng/chuẩn bị xong loại bánh này"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: '#FAF1DF', borderTop: '2px solid #DCC9A4', fontWeight: 800 }}>
                                    <td colSpan={3} style={{ textAlign: 'right', padding: '16px 18px', fontSize: '14px', color: '#241A16' }}>
                                        TỔNG MẺ {bData.weight}:
                                    </td>
                                    <td style={{ textAlign: 'center', color: '#976008' }}>
                                        {bData.totalEggs} quả trứng
                                    </td>
                                    <td style={{ textAlign: 'center', color: '#241A16' }}>
                                        {bData.totalCakes - bData.totalEggs} cái
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <span className="batch-qty-pill" style={{ background: is150 ? '#142C54' : '#B8862F' }}>
                                            {bData.totalCakes}
                                        </span>
                                    </td>
                                    <td colSpan={2} style={{ fontSize: '12.5px', color: '#6B5A4E' }}>
                                        {bData.totalNuong} nướng + {bData.totalDeo} dẻo · Bột vỏ: {bData.doughTotalKg} kg · Nhân: {bData.fillingTotalKg} kg
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : (
                    <div className="filling-grid">
                        {bData.fillings.map(f => (
                            <div key={f.filling} className="filling-card">
                                <div className="filling-card-header">
                                    <div className="filling-title">
                                        <i className={`fa-solid ${f.isNoFilling ? 'fa-bread-slice' : 'fa-bowl-rice'}`} style={{ color: '#B8862F', marginRight: '6px', fontSize: '14px' }}></i>
                                        {f.isNoFilling ? '' : 'Nhân '}{f.filling}{f.isNoFilling ? ' (Thuần vỏ)' : ''}
                                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#8F7A69', marginLeft: '6px' }}>({bData.weight})</span>
                                    </div>
                                    <div className="filling-total-badge" style={{ color: is150 ? '#142C54' : '#B8862F' }}>
                                        {f.totalQty} cái
                                    </div>
                                </div>

                                {f.isNoFilling ? (
                                    <>
                                        <div className="filling-stat-row">
                                            <span>Dòng bánh:</span>
                                            <strong style={{ color: '#1E7E34' }}>{f.deoQty > 0 ? `${f.deoQty} bánh dẻo` : ''} {f.nuongQty > 0 ? `${f.nuongQty} bánh nướng` : ''}</strong>
                                        </div>
                                        <div className="filling-stat-row">
                                            <span>Nhân bánh:</span>
                                            <strong style={{ color: '#28A745' }}>0g nhân (Thuần vỏ)</strong>
                                        </div>
                                        <div className="filling-stat-row">
                                            <span>Bột vỏ dẻo cần trộn:</span>
                                            <strong style={{ color: '#142C54' }}>~{f.doughKg} kg ({bData.weight}/cái)</strong>
                                        </div>

                                        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #F0E4CE', fontSize: '12.5px', color: '#241A16', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>Bánh không nhân:</span>
                                            <strong style={{ color: '#28A745', fontSize: '14px' }}>0 kg nhân cần cân</strong>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="filling-stat-row">
                                            <span>Bánh nướng:</span>
                                            <strong style={{ color: '#142C54' }}>{f.nuongQty} chiếc ({f.nuongQty * (is150 ? 95 : 115)}g)</strong>
                                        </div>
                                        <div className="filling-stat-row">
                                            <span>Bánh dẻo:</span>
                                            <strong style={{ color: '#1E7E34' }}>{f.deoQty} chiếc ({f.deoQty * (is150 ? 75 : 90)}g)</strong>
                                        </div>
                                        <div className="filling-stat-row">
                                            <span>Lòng đỏ trứng muối:</span>
                                            <strong style={{ color: '#B8862F' }}>{f.eggCount} quả</strong>
                                        </div>

                                        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #F0E4CE', fontSize: '12.5px', color: '#241A16' }}>
                                            Nhân cần cân viên mẻ {bData.weight}: <strong style={{ color: '#142C54', fontSize: '14px' }}>~{f.fillingKg} kg</strong>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Render Login Screen if not authenticated
    if (!isAuthenticated) {
        return (
            <div className="admin-login-wrapper">
                <div className="admin-login-bg-overlay" />
                <div className="admin-login-card">
                    <div className="admin-login-brand">
                        <div className="admin-login-logo-glow" />
                        <img
                            src={process.env.PUBLIC_URL + '/imgs/logo.png'}
                            alt="Cookie Shop"
                            className="admin-login-logo"
                        />
                    </div>

                    <div className="admin-login-header">
                        <h2 className="admin-login-title">Quản Trị Cookie Shop</h2>
                        <p className="admin-login-sub">Đăng nhập để quản lý đơn bánh &amp; cấu hình tiệm</p>
                    </div>

                    {loginError && (
                        <div className="admin-login-error">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <span>{loginError}</span>
                        </div>
                    )}

                    <form onSubmit={handleLogin} className="admin-login-form">
                        <div className="admin-login-field">
                            <label className="admin-login-label">Tài khoản</label>
                            <div className="admin-login-input-wrap">
                                <i className="fa-solid fa-user admin-login-input-icon" />
                                <input
                                    type="text"
                                    className="admin-login-input"
                                    placeholder="Nhập tài khoản quản trị"
                                    value={loginUser}
                                    onChange={(e) => {
                                        setLoginUser(e.target.value);
                                        setLoginError('');
                                    }}
                                    autoComplete="username"
                                    autoFocus
                                    required
                                />
                            </div>
                        </div>

                        <div className="admin-login-field">
                            <label className="admin-login-label">Mật khẩu</label>
                            <div className="admin-login-input-wrap">
                                <i className="fa-solid fa-lock admin-login-input-icon" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className="admin-login-input"
                                    placeholder="Nhập mật khẩu quản trị"
                                    value={loginPass}
                                    onChange={(e) => {
                                        setLoginPass(e.target.value);
                                        setLoginError('');
                                    }}
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="admin-login-eye-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                                >
                                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                                </button>
                            </div>
                        </div>

                        <div className="admin-login-opts">
                            <label className="admin-login-remember">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                                <span>Ghi nhớ đăng nhập</span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="admin-login-submit-btn"
                            disabled={isLoggingIn}
                        >
                            {isLoggingIn ? (
                                <><i className="fa-solid fa-spinner spin" style={{ marginRight: 8 }} /> Đang đăng nhập...</>
                            ) : (
                                <><i className="fa-solid fa-arrow-right-to-bracket" style={{ marginRight: 8 }} /> Đăng nhập hệ thống</>
                            )}
                        </button>
                    </form>

                    <div className="admin-login-foot">
                        <Link to="/" className="admin-login-back-link">
                            <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} /> Quay về trang chủ tiệm bánh
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-container">
            {/* SIDEBAR */}
            <aside className="admin-sidebar">
                <div className="admin-sidebar-header">
                    <img 
                        src={process.env.PUBLIC_URL + '/imgs/logo.png'} 
                        alt="Cookie Shop" 
                        style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 0 0 1.5px #D4AF37' }} 
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: 700, color: '#FDF7EA' }}>Cookie Shop</div>
                        <div style={{ fontSize: '10px', letterSpacing: '.16em', textTransform: 'uppercase', color: '#8F7A69' }}>Quản trị mùa 2026</div>
                    </div>
                    <button
                        type="button"
                        className="admin-sidebar-logout-btn"
                        onClick={handleLogout}
                        title="Đăng xuất khỏi hệ thống"
                    >
                        <i className="fa-solid fa-right-from-bracket" />
                    </button>
                </div>

                <div className="admin-nav-list">
                    <button
                        type="button"
                        className={`admin-nav-btn ${currentTab === 'schedule' ? 'active' : ''}`}
                        onClick={() => setCurrentTab('schedule')}
                    >
                        <i className="fa-solid fa-calendar-days" style={{ width: '18px' }}></i>
                        <span>Lịch giao hàng</span>
                        <span className="admin-nav-badge">{bills.length}</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-btn ${currentTab === 'batch' ? 'active' : ''}`}
                        onClick={() => setCurrentTab('batch')}
                    >
                        <i className="fa-solid fa-kitchen-set" style={{ width: '18px' }}></i>
                        <span>Tổng hợp làm bánh</span>
                        <span className="admin-nav-badge">{batchSummary.totalCakes}</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-btn ${currentTab === 'orders' ? 'active' : ''}`}
                        onClick={() => setCurrentTab('orders')}
                    >
                        <i className="fa-solid fa-receipt" style={{ width: '18px' }}></i>
                        <span>Đơn đặt trước</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-btn ${currentTab === 'products' ? 'active' : ''}`}
                        onClick={() => setCurrentTab('products')}
                    >
                        <i className="fa-solid fa-cookie-bite" style={{ width: '18px' }}></i>
                        <span>Menu &amp; hộp</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-btn ${currentTab === 'sepay' ? 'active' : ''}`}
                        onClick={() => setCurrentTab('sepay')}
                    >
                        <i className="fa-solid fa-qrcode" style={{ width: '18px' }}></i>
                        <span>Cài đặt SePay</span>
                    </button>

                    <button
                        type="button"
                        className={`admin-nav-btn ${currentTab === 'recipe' ? 'active' : ''}`}
                        onClick={() => setCurrentTab('recipe')}
                    >
                        <i className="fa-solid fa-scale-balanced" style={{ width: '18px' }}></i>
                        <span>Công thức &amp; Định lượng</span>
                    </button>
                </div>

                {/* ORDER INTAKE STATUS WIDGET */}
                <div className="admin-sidebar-order-widget">
                    <div className="admin-order-widget-top">
                        <div className="admin-order-widget-title">
                            <span className={`admin-order-status-dot ${orderEnabled ? 'online' : 'offline'}`} />
                            <span>Nhận đơn mới</span>
                        </div>
                        <span className={`admin-order-status-badge ${orderEnabled ? 'online' : 'offline'}`}>
                            {orderEnabled ? 'ĐANG MỞ' : 'ĐÃ ĐÓNG'}
                        </span>
                    </div>

                    <div className="admin-order-switch-row">
                        <div className="admin-order-switch-label">
                            {orderEnabled ? 'Khách đang đặt bình thường' : 'Đang hiện thông báo tạm dừng'}
                        </div>
                        <label className="admin-switch" title={orderEnabled ? 'Bấm để đóng nhận đơn' : 'Bấm để mở nhận đơn'}>
                            <input
                                type="checkbox"
                                checked={orderEnabled}
                                onChange={handleToggleOrderIntake}
                            />
                            <span className="admin-switch-slider" />
                        </label>
                    </div>

                    <button
                        type="button"
                        className="admin-order-widget-btn"
                        onClick={handleOpenOrderSettingsModal}
                    >
                        <i className="fa-solid fa-pen-to-square" style={{ marginRight: 6 }} />
                        <span>Lời nhắn tạm dừng...</span>
                    </button>
                </div>

                <div className="admin-sidebar-footer">
                    <div style={{ fontSize: '12px', color: '#8F7A69', lineHeight: 1.6 }}>
                        3 đợt giao: <strong style={{ color: '#F6E7C8' }}>14 · 18 · 23.09.2026</strong><br />
                        Chốt đơn trước 48h để làm bánh
                    </div>
                    <Link to="/" style={{ display: 'inline-block', marginTop: '12px', fontSize: '12.5px', color: '#B8862F' }}>
                        <i className="fa-solid fa-arrow-left" style={{ marginRight: '6px' }}></i>
                        Xem trang khách hàng
                    </Link>
                </div>
            </aside>

            {/* MAIN WORKSPACE */}
            <main className="admin-main">
                {/* Global alert banner when orders are paused */}
                {!orderEnabled && (
                    <div className="admin-paused-alert-bar">
                        <div className="admin-paused-alert-content">
                            <span className="paused-pulse-dot" style={{ width: 8, height: 8, flexShrink: 0 }} />
                            <div>
                                <strong style={{ color: '#CF1322' }}>HỆ THỐNG ĐANG TẠM DỪNG NHẬN ĐƠN:</strong>
                                <span style={{ marginLeft: 6 }}>Trang đặt bánh của khách đang hiển thị thông báo tạm dừng và khóa nút đặt hàng.</span>
                            </div>
                        </div>
                        <div className="admin-paused-alert-actions">
                            <button
                                type="button"
                                className="btn-lacquer"
                                style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '6px' }}
                                onClick={handleToggleOrderIntake}
                            >
                                <i className="fa-solid fa-play" style={{ marginRight: 6 }} /> Mở nhận đơn ngay
                            </button>
                            <button
                                type="button"
                                className="btn-outline-paper"
                                style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px' }}
                                onClick={handleOpenOrderSettingsModal}
                            >
                                <i className="fa-solid fa-gear" style={{ marginRight: 6 }} /> Sửa lời nhắn
                            </button>
                        </div>
                    </div>
                )}

                {/* 1. SCHEDULE & DELIVERY VIEW */}
                {currentTab === 'schedule' && (
                    <>
                        <div className="admin-top-bar">
                            <div>
                                <h2 style={{ margin: '0 0 6px', fontFamily: 'Playfair Display, serif', fontSize: '30px', fontWeight: 700, color: '#241A16' }}>
                                    Lịch sản xuất &amp; giao hàng
                                </h2>
                                <p style={{ margin: 0, fontSize: '13.5px', color: '#7A6A5F' }}>
                                    Đơn đặt trước được gom theo ngày giao để xưởng lên kế hoạch nướng bánh mỗi ngày
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    type="button"
                                    className="btn-outline-paper"
                                    style={{ borderRadius: '20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
                                    onClick={() => handleOpenBatchPrint('all')}
                                    title="In toàn bộ hóa đơn chuẩn khổ A4 cắt rời"
                                >
                                    <i className="fa-solid fa-print" style={{ color: '#142C54' }}></i>
                                    In bill theo ngày
                                </button>
                                <button type="button" className="btn-lacquer" style={{ borderRadius: '20px' }} onClick={() => handleOpenProductModal()}>
                                    <i className="fa-solid fa-plus" style={{ marginRight: '6px' }}></i>
                                    Thêm bánh mới
                                </button>
                            </div>
                        </div>

                        {/* 4 STAT CARDS */}
                        <div className="admin-stats-grid">
                            <div className="admin-stat-card">
                                <div className="stat-label">Doanh thu đã chốt</div>
                                <div className="stat-value" style={{ color: '#241A16' }}>
                                    {formatCurrency(totalRevenue)}
                                </div>
                                <div className="stat-note">Mùa 2026 ({paidBills.length} đơn đã thanh toán)</div>
                            </div>

                            <div className="admin-stat-card">
                                <div className="stat-label">Đơn chờ thanh toán</div>
                                <div className="stat-value" style={{ color: '#142C54' }}>
                                    {pendingBills.length}
                                </div>
                                <div className="stat-note">Chờ chuyển khoản / Thanh toán sau</div>
                            </div>

                            <div className="admin-stat-card">
                                <div className="stat-label">Tổng đơn đã đặt</div>
                                <div className="stat-value" style={{ color: '#241A16' }}>
                                    {bills.length}
                                </div>
                                <div className="stat-note">Bao gồm hộp quà và bánh lẻ</div>
                            </div>

                            <div className="admin-stat-card">
                                <div className="stat-label">Vị bánh đang mở</div>
                                <div className="stat-value" style={{ color: '#B8862F' }}>
                                    {products.length}
                                </div>
                                <div className="stat-note">Sẵn sàng nhận đặt mùa 2026</div>
                            </div>
                        </div>

                        {/* SCHEDULE PANEL (GROUPED BY DATE) */}
                        <div className="schedule-panel">
                            <div className="schedule-panel-header">
                                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: 700, color: '#241A16' }}>
                                    Đơn gom theo ngày nhận bánh
                                </div>
                                <div style={{ display: 'flex', gap: '7px' }}>
                                    {[
                                        { id: 'all', label: 'Tất cả' },
                                        { id: 'unpaid', label: 'Chưa thanh toán' },
                                        { id: 'paid', label: 'Đã thanh toán' },
                                        { id: 'make', label: 'Đang làm bánh' },
                                        { id: 'done', label: 'Đã giao' }
                                    ].map(f => (
                                        <button
                                            key={f.id}
                                            type="button"
                                            className={`orders-tab-btn ${statusFilter === f.id ? 'active' : ''}`}
                                            onClick={() => setStatusFilter(f.id)}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '50px 0', color: '#7A6A5F' }}>
                                    <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '24px', color: '#142C54', marginBottom: '8px' }}></i>
                                    <div>Đang đồng bộ dữ liệu đơn hàng...</div>
                                </div>
                            ) : Object.keys(groupedByDate).length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '50px 0', color: '#7A6A5F' }}>
                                    Chưa có đơn hàng nào trong khoảng thời gian này.
                                </div>
                            ) : (
                                Object.entries(groupedByDate).map(([dateKey, groupBills]) => {
                                    const groupRevenue = groupBills.reduce((sum, b) => sum + (Number(b.totalAmount) || 0), 0);
                                    return (
                                        <div key={dateKey}>
                                            <div className="date-group-header">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                                    <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '17px', fontWeight: 700, color: '#241A16' }}>
                                                        {dateKey === 'Chưa định ngày' ? dateKey : formatDateShort(dateKey)}
                                                    </span>
                                                    <span style={{ fontSize: '12.5px', color: '#6B5A4E' }}>
                                                        {groupBills.length} đơn đặt trước
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#142C54' }}>
                                                        {formatCurrency(groupRevenue)}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="btn-outline-paper"
                                                        style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '14px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenBatchPrint(dateKey);
                                                        }}
                                                        title={`In toàn bộ ${groupBills.length} hóa đơn ngày ${dateKey}`}
                                                    >
                                                        <i className="fa-solid fa-print" style={{ color: '#142C54' }}></i>
                                                        In {groupBills.length} bill ngày này
                                                    </button>
                                                </div>
                                            </div>

                                            {groupBills.map((r) => (
                                                <div key={r.id} className="order-table-row">
                                                    <strong style={{ color: '#241A16', fontSize: '14px' }}>
                                                        #{r.orderCode}
                                                    </strong>

                                                    <div>
                                                        <div style={{ fontWeight: 700, color: '#241A16' }}>{r.customerName}</div>
                                                        <div style={{ fontSize: '12px', color: '#8B7A6D' }}>
                                                            {r.customerPhone || 'Chưa có SĐT'} · {r.deliveryTimeSlot}
                                                        </div>
                                                    </div>

                                                    <div style={{ color: '#5C4A3F', fontSize: '12.5px', lineHeight: 1.45 }}>
                                                        {parseItemsSummary(r.items)}
                                                        {r.comment_user && (
                                                            <div style={{ fontSize: '11.5px', color: '#142C54', fontStyle: 'italic', marginTop: '2px' }}>
                                                                Ghi chú: {r.comment_user}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <strong style={{ color: '#241A16' }}>{formatCurrency(r.totalAmount)}</strong>
                                                        {r.paymentMethod === 'pay_later' && (
                                                            <div style={{ fontSize: '10.5px', color: '#B45309', fontWeight: 600 }}>
                                                                <i className="fa-regular fa-clock" style={{ marginRight: 3 }} /> Thanh toán sau
                                                            </div>
                                                        )}
                                                        {renderPayTag(r)}
                                                    </div>

                                                    <div>
                                                        <select
                                                            className="status-select-badge"
                                                            value={r.status || 'Chờ chuyển khoản'}
                                                            onChange={(e) => handleStatusChange(r.id, e.target.value)}
                                                        >
                                                            <option value="Chờ chuyển khoản">Chờ chuyển khoản</option>
                                                            <option value="Chờ thanh toán">Chờ thanh toán (Sau)</option>
                                                            <option value="Đã thanh toán">Đã thanh toán</option>
                                                            <option value="Đang làm bánh">Đang làm bánh</option>
                                                            <option value="Đang giao hàng">Đang giao hàng</option>
                                                            <option value="Đã giao hàng">Đã giao hàng</option>
                                                            <option value="Đã hủy">Đã hủy</option>
                                                        </select>
                                                    </div>

                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button
                                                            type="button"
                                                            className="btn-outline-paper"
                                                            style={{ padding: '5px 9px', fontSize: '12px' }}
                                                            onClick={() => setDetailBill(r)}
                                                        >
                                                            Xem
                                                        </button>
                                                        <button
                                                            type="button"
                                                            style={{ background: 'none', border: 'none', color: '#C3B49F', cursor: 'pointer', padding: '5px' }}
                                                            onClick={() => handleDeleteBill(r.id)}
                                                            title="Xoá đơn"
                                                        >
                                                            <i className="fa-regular fa-trash-can"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </>
                )}

                {/* 2. BATCH PRODUCTION VIEW (TỔNG HỢP LÀM BÁNH THEO MẺ) */}
                {currentTab === 'batch' && (
                    <>
                        <div className="admin-top-bar">
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.14em', color: '#142C54', background: '#EEF4FB', padding: '3px 9px', borderRadius: '6px' }}>
                                        Dành cho xưởng bánh
                                    </span>
                                    <span style={{ fontSize: '12px', color: '#7A6A5F' }}>
                                        {batchSummary.totalBills} đơn cấu thành mẻ này
                                    </span>
                                </div>
                                <h2 style={{ margin: '0 0 6px', fontFamily: 'Playfair Display, serif', fontSize: '30px', fontWeight: 700, color: '#241A16' }}>
                                    Tổng hợp làm bánh theo mẻ
                                </h2>
                                <p style={{ margin: 0, fontSize: '13.5px', color: '#7A6A5F' }}>
                                    Tách biệt rõ ràng mẻ 150g (khuôn chuẩn) và mẻ 180g (khuôn đại) do khác biệt khối lượng vỏ và nhân.
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button type="button" className="batch-action-btn wipe" onClick={handleDeleteTestBills} title="Xoá tất cả các đơn hàng thử nghiệm mã TEST* khỏi hệ thống">
                                    <i className="fa-regular fa-trash-can"></i>
                                    Xóa đơn test
                                </button>
                                <button type="button" className="batch-action-btn copy" onClick={handleCopyBatchSummary} title="Sao chép bảng tóm tắt để gửi Zalo cho thợ xưởng">
                                    <i className="fa-regular fa-copy"></i>
                                    Sao chép mẻ bánh
                                </button>
                                <button type="button" className="batch-action-btn print" onClick={handlePrintBatch} title="In phiếu làm bánh chuẩn khổ A4">
                                    <i className="fa-solid fa-print"></i>
                                    In phiếu làm mẻ (A4)
                                </button>
                                <button
                                    type="button"
                                    className="batch-action-btn"
                                    style={{ background: '#F8F4ED', borderColor: '#D9C8B4', color: '#142C54' }}
                                    onClick={() => handleOpenBatchPrint(batchDateFilter === 'all' ? 'all' : batchDateFilter)}
                                    title="In toàn bộ hóa đơn / tem dán hộp chuẩn khổ A4 cắt rời"
                                >
                                    <i className="fa-solid fa-receipt"></i>
                                    In bill theo mẻ (A4)
                                </button>
                                <button
                                    type="button"
                                    className="batch-action-btn"
                                    style={{ background: '#142C54', color: '#FFF', borderColor: '#142C54' }}
                                    onClick={() => setCurrentTab('recipe')}
                                    title="Tính định lượng vỏ, nhân và sốt theo số bánh mẻ này"
                                >
                                    <i className="fa-solid fa-scale-balanced" style={{ marginRight: '6px' }}></i>
                                    Cân công thức &amp; sốt
                                </button>
                            </div>
                        </div>

                        {/* 5 KPI CARDS - TỔNG TOÀN BỘ */}
                        <div className="batch-kpi-grid">
                            <div className="batch-kpi-card highlight">
                                <i className="fa-solid fa-cake-candles batch-kpi-icon"></i>
                                <div className="batch-kpi-label">Tổng bánh cần làm</div>
                                <div className="batch-kpi-num">{batchSummary.totalCakes}</div>
                                <div className="batch-kpi-sub">Chiếc bánh của mẻ này ({batchSummary.totalBills} đơn)</div>
                            </div>

                            <div className="batch-kpi-card">
                                <i className="fa-solid fa-fire-burner batch-kpi-icon" style={{ color: '#142C54' }}></i>
                                <div className="batch-kpi-label">Bánh Nướng</div>
                                <div className="batch-kpi-num" style={{ color: '#142C54' }}>{batchSummary.totalNuong}</div>
                                <div className="batch-kpi-sub">150g: {batchSummary.batch150.totalNuong} · 180g: {batchSummary.batch180.totalNuong}</div>
                            </div>

                            <div className="batch-kpi-card">
                                <i className="fa-solid fa-spa batch-kpi-icon" style={{ color: '#1E7E34' }}></i>
                                <div className="batch-kpi-label">Bánh Dẻo</div>
                                <div className="batch-kpi-num" style={{ color: '#1E7E34' }}>{batchSummary.totalDeo}</div>
                                <div className="batch-kpi-sub">150g: {batchSummary.batch150.totalDeo} · 180g: {batchSummary.batch180.totalDeo}</div>
                            </div>

                            <div className="batch-kpi-card">
                                <i className="fa-solid fa-egg batch-kpi-icon" style={{ color: '#B8862F' }}></i>
                                <div className="batch-kpi-label">Lòng đỏ trứng muối</div>
                                <div className="batch-kpi-num" style={{ color: '#B8862F' }}>{batchSummary.totalEggs}</div>
                                <div className="batch-kpi-sub">150g: {batchSummary.batch150.totalEggs} · 180g: {batchSummary.batch180.totalEggs}</div>
                            </div>

                            <div className="batch-kpi-card">
                                <i className="fa-solid fa-gift batch-kpi-icon" style={{ color: '#7A6A5F' }}></i>
                                <div className="batch-kpi-label">Vỏ Hộp Quà</div>
                                <div className="batch-kpi-num" style={{ color: '#241A16' }}>
                                    {Object.values(batchSummary.packaging).reduce((a, b) => a + b, 0)}
                                </div>
                                <div className="batch-kpi-sub">
                                    {Object.entries(batchSummary.packaging).map(([k, v]) => `${v} ${k}`).join(' · ') || 'Chưa có hộp quà'}
                                </div>
                            </div>
                        </div>

                        {/* FILTER BAR & VIEW TOGGLE */}
                        <div className="batch-filter-panel">
                            <div className="batch-filter-group">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <i className="fa-solid fa-calendar-day" style={{ color: '#142C54', fontSize: '13px' }}></i>
                                    <span style={{ fontSize: '13px', fontWeight: 700 }}>Ngày giao:</span>
                                    <select
                                        className="batch-select"
                                        value={batchDateFilter}
                                        onChange={(e) => setBatchDateFilter(e.target.value)}
                                    >
                                        <option value="all">Tất cả các mẻ ({batchEligibleBills.length} đơn)</option>
                                        <option value="today">Mẻ giao hôm nay</option>
                                        <option value="tomorrow">Mẻ giao ngày mai</option>
                                        {batchAvailableDates.map(d => (
                                            <option key={d} value={d}>
                                                Ngày {formatDateShort(d)} ({batchEligibleBills.filter(b => (b.delivery_date || '').startsWith(d)).length} đơn)
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <i className="fa-regular fa-clock" style={{ color: '#B8862F', fontSize: '13px' }}></i>
                                    <span style={{ fontSize: '13px', fontWeight: 700 }}>Khung giờ:</span>
                                    <select
                                        className="batch-select"
                                        value={batchSlotFilter}
                                        onChange={(e) => setBatchSlotFilter(e.target.value)}
                                    >
                                        <option value="all">Tất cả khung giờ</option>
                                        <option value="09:00 – 12:00">09:00 – 12:00</option>
                                        <option value="14:00 – 17:00">14:00 – 17:00</option>
                                        <option value="18:00 – 21:00">18:00 – 21:00</option>
                                    </select>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#7A6A5F', fontSize: '13px' }}></i>
                                    <span style={{ fontSize: '13px', fontWeight: 700 }}>Trạng thái:</span>
                                    <select
                                        className="batch-select"
                                        value={batchStatusFilter}
                                        onChange={(e) => setBatchStatusFilter(e.target.value)}
                                    >
                                        <option value="all_active">Tất cả đơn chưa giao (Mặc định)</option>
                                        <option value="paid_only">Chỉ đơn đã thanh toán (chưa giao)</option>
                                        <option value="pending_only">Chỉ đơn chờ chuyển khoản (chưa giao)</option>
                                        <option value="delivered_only">Chỉ đơn đã giao hàng</option>
                                        <option value="all_including_delivered">Tất cả đơn (gồm cả đã giao)</option>
                                    </select>
                                </div>

                                {/* BỘ LỌC THEO ĐƠN HÀNG */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <i className="fa-solid fa-receipt" style={{ color: '#142C54', fontSize: '13px' }}></i>
                                    <span style={{ fontSize: '13px', fontWeight: 700 }}>Đơn hàng:</span>
                                    <select
                                        className="batch-select"
                                        value={batchOrderFilter}
                                        onChange={(e) => setBatchOrderFilter(e.target.value)}
                                        style={{ maxWidth: '240px' }}
                                    >
                                        <option value="all">Tất cả đơn hàng ({batchSummary.candidateBillsForBatch.length} đơn)</option>
                                        {batchSummary.candidateBillsForBatch.map(b => (
                                            <option key={b.id} value={b.orderCode}>
                                                #{b.orderCode} - {b.customerName || 'Khách'} ({countCakesInBill(b)} bánh)
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* BỘ LỌC THEO VỊ NHÂN */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <i className="fa-solid fa-bowl-rice" style={{ color: '#B8862F', fontSize: '13px' }}></i>
                                    <span style={{ fontSize: '13px', fontWeight: 700 }}>Vị nhân:</span>
                                    <select
                                        className="batch-select"
                                        value={batchFillingFilter}
                                        onChange={(e) => setBatchFillingFilter(e.target.value)}
                                    >
                                        <option value="all">Tất cả vị nhân ({batchSummary.availableFillings.length} vị)</option>
                                        {batchSummary.availableFillings.map(f => (
                                            <option key={f.filling} value={f.filling}>
                                                {f.filling} ({f.totalQty} chiếc)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* View Switcher */}
                            <div style={{ display: 'flex', gap: '6px', background: '#F4E9D5', padding: '4px', borderRadius: '10px' }}>
                                <button
                                    type="button"
                                    className={`orders-tab-btn ${batchViewMode === 'cakes' ? 'active' : ''}`}
                                    onClick={() => setBatchViewMode('cakes')}
                                    style={{ borderRadius: '8px', padding: '6px 12px', fontSize: '12.5px' }}
                                >
                                    <i className="fa-solid fa-list-ol" style={{ marginRight: '6px' }}></i>
                                    Từng món bánh
                                </button>
                                <button
                                    type="button"
                                    className={`orders-tab-btn ${batchViewMode === 'fillings' ? 'active' : ''}`}
                                    onClick={() => setBatchViewMode('fillings')}
                                    style={{ borderRadius: '8px', padding: '6px 12px', fontSize: '12.5px' }}
                                >
                                    <i className="fa-solid fa-bowl-rice" style={{ marginRight: '6px' }}></i>
                                    Theo vị nhân &amp; khối lượng
                                </button>
                            </div>
                        </div>

                        {/* THANH CHỌN NHANH VỊ NHÂN (CHIP FILTER) */}
                        {batchSummary.allConsolidatedFillings.length > 0 && (
                            <div className="batch-filling-bar">
                                <span className="batch-filling-bar-title">
                                    <i className="fa-solid fa-bowl-rice" style={{ color: '#B8862F' }}></i>
                                    Lọc theo nhân:
                                </span>
                                <button
                                    type="button"
                                    className={`batch-filling-chip ${batchFillingFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => setBatchFillingFilter('all')}
                                >
                                    <i className="fa-solid fa-layer-group"></i>
                                    Tất cả nhân ({batchSummary.rawTotalCakes} bánh)
                                </button>
                                {batchSummary.allConsolidatedFillings.map(f => (
                                    <button
                                        key={f.filling}
                                        type="button"
                                        className={`batch-filling-chip ${batchFillingFilter === f.filling ? 'active' : ''}`}
                                        onClick={() => setBatchFillingFilter(f.filling)}
                                    >
                                        <span>{f.filling}</span>
                                        <strong className="chip-badge">{f.totalQty}</strong>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* BANNER THÔNG BÁO BỘ LỌC ĐANG BẬT */}
                        {(batchOrderFilter !== 'all' || batchFillingFilter !== 'all') && (
                            <div className="batch-active-filter-alert">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#142C54', fontSize: '15px' }}></i>
                                    <span style={{ fontSize: '13.5px', color: '#241A16' }}>
                                        Đang lọc mẻ:
                                        {batchOrderFilter !== 'all' && (
                                            <span className="filter-tag-pill">
                                                Đơn <strong>#{batchOrderFilter}</strong>
                                                <i className="fa-solid fa-xmark remove-filter" onClick={() => setBatchOrderFilter('all')} title="Bỏ lọc đơn"></i>
                                            </span>
                                        )}
                                        {batchFillingFilter !== 'all' && (
                                            <span className="filter-tag-pill filling">
                                                Nhân <strong>{batchFillingFilter}</strong>
                                                <i className="fa-solid fa-xmark remove-filter" onClick={() => setBatchFillingFilter('all')} title="Bỏ lọc nhân"></i>
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className="btn-clear-all-filters"
                                    onClick={() => { setBatchOrderFilter('all'); setBatchFillingFilter('all'); }}
                                >
                                    <i className="fa-solid fa-rotate-left" style={{ marginRight: '6px' }}></i>
                                    Xem lại toàn bộ mẻ
                                </button>
                            </div>
                        )}

                        {/* WEIGHT SELECTOR TABS (150g vs 180g) */}
                        <div className="batch-weight-tabs">
                            <button
                                type="button"
                                className={`batch-weight-pill ${batchWeightFilter === 'all' ? 'active' : ''}`}
                                onClick={() => setBatchWeightFilter('all')}
                            >
                                <i className="fa-solid fa-layer-group"></i>
                                Tất cả các cỡ bánh ({batchSummary.totalCakes} chiếc)
                            </button>
                            <button
                                type="button"
                                className={`batch-weight-pill pill-150 ${batchWeightFilter === '150g' ? 'active' : ''}`}
                                onClick={() => setBatchWeightFilter('150g')}
                            >
                                <i className="fa-solid fa-cookie"></i>
                                Mẻ 150g Tiêu chuẩn ({batchSummary.batch150.totalCakes} bánh)
                            </button>
                            <button
                                type="button"
                                className={`batch-weight-pill pill-180 ${batchWeightFilter === '180g' ? 'active' : ''}`}
                                onClick={() => setBatchWeightFilter('180g')}
                            >
                                <i className="fa-solid fa-crown"></i>
                                Mẻ 180g Bánh đại ({batchSummary.batch180.totalCakes} bánh)
                            </button>
                        </div>

                        {/* PACKAGING BANNER */}
                        {Object.keys(batchSummary.packaging).length > 0 && (
                            <div className="packaging-banner">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#142C54', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#DFB953', fontSize: '20px' }}>
                                        <i className="fa-solid fa-box-open"></i>
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 800, fontSize: '15px', color: '#241A16' }}>
                                            Kế hoạch chuẩn bị Vỏ Hộp &amp; Túi xách
                                        </div>
                                        <div style={{ fontSize: '12.5px', color: '#6B5A4E' }}>
                                            Chuẩn bị đủ vỏ hộp trước khi đóng mẻ bánh ra lò
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    {Object.entries(batchSummary.packaging).map(([boxName, boxCount]) => (
                                        <div key={boxName} style={{ background: '#FFF', border: '1px solid #DCC9A4', borderRadius: '10px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ fontWeight: 600, fontSize: '13.5px', color: '#241A16' }}>{boxName}:</span>
                                            <strong style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#142C54' }}>
                                                {boxCount} hộp
                                            </strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* EMPTY STATE IF NO CAKES */}
                        {batchSummary.totalCakes === 0 ? (
                            <div className="batch-table-wrapper" style={{ padding: '60px 20px', textAlign: 'center' }}>
                                <i className="fa-solid fa-cookie-bite" style={{ fontSize: '40px', color: '#C3B49F', marginBottom: '14px' }}></i>
                                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#241A16', margin: '0 0 6px' }}>
                                    Không có bánh nào phù hợp
                                </h3>
                                <p style={{ color: '#7A6A5F', fontSize: '13.5px', margin: '0 0 16px' }}>
                                    {batchOrderFilter !== 'all' || batchFillingFilter !== 'all' 
                                        ? 'Không tìm thấy bánh nào khớp với đơn hàng hoặc vị nhân bạn đang lọc.' 
                                        : (batchDateFilter !== 'all' 
                                            ? 'Không tìm thấy đơn đặt hàng hợp lệ cho ngày hoặc bộ lọc đã chọn. Hãy chọn "Tất cả các mẻ".' 
                                            : 'Chưa có đơn đặt hàng nào trong hệ thống.')}
                                </p>
                                {(batchOrderFilter !== 'all' || batchFillingFilter !== 'all') && (
                                    <button
                                        type="button"
                                        className="btn-lacquer"
                                        style={{ borderRadius: '20px', padding: '8px 18px', fontSize: '13px' }}
                                        onClick={() => { setBatchOrderFilter('all'); setBatchFillingFilter('all'); }}
                                    >
                                        <i className="fa-solid fa-rotate-left" style={{ marginRight: '6px' }}></i>
                                        Bỏ lọc đơn &amp; nhân
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                {/* TAB TẤT CẢ: NẾU XEM THEO NHÂN THÌ HIỂN THỊ KHỐI TỔNG HỢP NHÂN TOÀN MẺ (GỘP CẢ 150G & 180G) */}
                                {batchWeightFilter === 'all' && batchViewMode === 'fillings' && (
                                    <div className="consolidated-section">
                                        <div className="consolidated-header">
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.14em', color: '#B8862F', background: '#FAF1DF', padding: '3px 9px', borderRadius: '6px', border: '1px solid #DCC9A4' }}>
                                                        Toàn bộ xưởng bánh
                                                    </span>
                                                    <span style={{ fontSize: '12.5px', color: '#7A6A5F' }}>
                                                        {batchSummary.consolidatedFillings.length} vị nhân cần chuẩn bị
                                                    </span>
                                                </div>
                                                <h3 style={{ margin: 0, fontFamily: 'Playfair Display, serif', fontSize: '22px', fontWeight: 700, color: '#241A16' }}>
                                                    Tổng Hợp Nhân Toàn Mẻ (Gộp cả 150g &amp; 180g)
                                                </h3>
                                                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#7A6A5F' }}>
                                                    Tổng hợp số bánh, khối lượng nhân cần sên/cân và trứng muối gộp cả 2 cỡ bánh để thợ chuẩn bị nguyên liệu nhanh nhất.
                                                </p>
                                            </div>
                                            {batchFillingFilter !== 'all' && (
                                                <button
                                                    type="button"
                                                    className="btn-clear-all-filters"
                                                    onClick={() => setBatchFillingFilter('all')}
                                                >
                                                    <i className="fa-solid fa-layer-group" style={{ marginRight: '6px' }}></i>
                                                    Xem tất cả các vị nhân
                                                </button>
                                            )}
                                        </div>

                                        <div className="consolidated-grid">
                                            {batchSummary.consolidatedFillings.map(f => (
                                                <div key={f.filling} className="consolidated-card">
                                                    <div>
                                                        <div className="consolidated-card-top">
                                                            <div className="consolidated-card-title">
                                                                <i className={`fa-solid ${f.isNoFilling ? 'fa-bread-slice' : 'fa-bowl-rice'}`} style={{ color: '#B8862F', fontSize: '16px' }}></i>
                                                                {f.isNoFilling ? '' : 'Nhân '}{f.filling}{f.isNoFilling ? ' (Thuần vỏ)' : ''}
                                                            </div>
                                                            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: 800, color: '#142C54', background: '#EEF4FB', padding: '3px 12px', borderRadius: '8px' }}>
                                                                {f.totalQty} <span style={{ fontSize: '13px' }}>chiếc</span>
                                                            </div>
                                                        </div>

                                                        {/* Dòng mẻ 150g */}
                                                        <div className="consolidated-size-row s150">
                                                            <div>
                                                                <strong>Khuôn tiêu chuẩn 150g:</strong> {f.qty150} bánh
                                                                <div style={{ fontSize: '11px', color: '#6B7A94', marginTop: '2px' }}>
                                                                    {f.isNoFilling ? (
                                                                        f.qty150 > 0 ? `${f.qty150} dẻo (0g nhân · Bột vỏ 150g/cái)` : 'Không có bánh 150g'
                                                                    ) : (
                                                                        <>
                                                                            {f.nuong150 > 0 ? `${f.nuong150} nướng (~${f.nuong150 * 95}g)` : ''}
                                                                            {f.nuong150 > 0 && f.deo150 > 0 ? ' · ' : ''}
                                                                            {f.deo150 > 0 ? `${f.deo150} dẻo (~${f.deo150 * 75}g)` : ''}
                                                                            {f.qty150 === 0 && 'Không có bánh 150g'}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div style={{ textAlign: 'right', fontWeight: 700 }}>
                                                                {f.isNoFilling ? (
                                                                    <>
                                                                        <span style={{ color: '#28A745' }}>0 kg nhân</span>
                                                                        <div style={{ fontSize: '11px', color: '#142C54' }}>Vỏ: ~{f.doughKg150} kg</div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        ~{f.fillingKg150} kg
                                                                        <div style={{ fontSize: '11px', color: '#B8862F' }}>{f.egg150} quả trứng</div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Dòng mẻ 180g */}
                                                        <div className="consolidated-size-row s180">
                                                            <div>
                                                                <strong>Khuôn đại 180g:</strong> {f.qty180} bánh
                                                                <div style={{ fontSize: '11px', color: '#976008', marginTop: '2px' }}>
                                                                    {f.isNoFilling ? (
                                                                        f.qty180 > 0 ? `${f.qty180} dẻo (0g nhân · Bột vỏ 180g/cái)` : 'Không có bánh 180g'
                                                                    ) : (
                                                                        <>
                                                                            {f.nuong180 > 0 ? `${f.nuong180} nướng (~${f.nuong180 * 115}g)` : ''}
                                                                            {f.nuong180 > 0 && f.deo180 > 0 ? ' · ' : ''}
                                                                            {f.deo180 > 0 ? `${f.deo180} dẻo (~${f.deo180 * 90}g)` : ''}
                                                                            {f.qty180 === 0 && 'Không có bánh 180g'}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div style={{ textAlign: 'right', fontWeight: 700 }}>
                                                                {f.isNoFilling ? (
                                                                    <>
                                                                        <span style={{ color: '#28A745' }}>0 kg nhân</span>
                                                                        <div style={{ fontSize: '11px', color: '#142C54' }}>Vỏ: ~{f.doughKg180} kg</div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        ~{f.fillingKg180} kg
                                                                        <div style={{ fontSize: '11px', color: '#B8862F' }}>{f.egg180} quả trứng</div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Đơn hàng cần giao vị này */}
                                                        {f.orders && f.orders.length > 0 && (
                                                            <div style={{ marginTop: '10px' }}>
                                                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#7A6A5F', marginBottom: '4px' }}>
                                                                    Đơn đặt vị này:
                                                                </div>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                    {f.orders.map((ord, oIdx) => (
                                                                        <span
                                                                            key={oIdx}
                                                                            style={{ fontSize: '10.5px', background: '#FAF1DF', border: '1px solid #DCC9A4', padding: '1px 6px', borderRadius: '4px', color: '#241A16' }}
                                                                            title={`${ord.customerName || 'Khách'} · ${ord.qty} cái (${ord.weight} ${ord.category})`}
                                                                        >
                                                                            #{ord.orderCode} ({ord.qty})
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="consolidated-card-footer">
                                                        <div>
                                                            <div style={{ fontSize: '11.5px', color: '#7A6A5F', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>
                                                                {f.isNoFilling ? 'Bánh không nhân:' : 'Tổng nhân cần cân:'}
                                                            </div>
                                                            <div className="consolidated-card-total-kg">
                                                                {f.isNoFilling ? (
                                                                    <span style={{ fontSize: '15.5px', color: '#28A745', fontWeight: 800 }}>
                                                                        0 kg <span style={{ fontSize: '12px', fontWeight: 600, color: '#7A6A5F' }}>(Bột vỏ: ~{f.totalDoughKg} kg)</span>
                                                                    </span>
                                                                ) : (
                                                                    <>~{f.totalFillingKg} <span style={{ fontSize: '14px' }}>kg</span></>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <div style={{ fontSize: '11.5px', color: '#7A6A5F', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>
                                                                {f.isNoFilling ? 'Quy cách vỏ:' : 'Lòng đỏ trứng:'}
                                                            </div>
                                                            <div style={{ fontFamily: 'Playfair Display', fontSize: '16px', fontWeight: 800, color: f.isNoFilling ? '#142C54' : '#B8862F' }}>
                                                                {f.isNoFilling ? '100% bột vỏ dẻo' : `${f.eggTotal} quả`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* SECTION 1: MẺ 150G */}
                                {(batchWeightFilter === 'all' || batchWeightFilter === '150g') && (
                                    renderBatchSizeBlock(batchSummary.batch150, 'b150')
                                )}

                                {/* SECTION 2: MẺ 180G */}
                                {(batchWeightFilter === 'all' || batchWeightFilter === '180g') && (
                                    renderBatchSizeBlock(batchSummary.batch180, 'b180')
                                )}
                            </>
                        )}
                    </>
                )}

                {/* 2. ORDERS LIST VIEW */}
                {currentTab === 'orders' && (
                    <div className="schedule-panel">
                        <div className="schedule-panel-header">
                            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: 700, color: '#241A16' }}>
                                Toàn bộ đơn hàng ({bills.length})
                            </div>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    placeholder="Tìm theo mã đơn, khách hàng, SĐT..."
                                    value={searchOrderQuery}
                                    onChange={(e) => setSearchOrderQuery(e.target.value)}
                                    style={{ border: '1px solid #DCC9A4', padding: '6px 12px', fontSize: '13px', borderRadius: '4px', width: '260px' }}
                                />
                                <button
                                    type="button"
                                    className="btn-outline-paper"
                                    style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}
                                    onClick={() => handleOpenBatchPrint('all')}
                                    title="In toàn bộ hóa đơn chuẩn khổ A4 cắt rời"
                                >
                                    <i className="fa-solid fa-print" style={{ color: '#142C54' }}></i>
                                    In toàn bộ bill (A4)
                                </button>
                            </div>
                        </div>

                        {bills
                            .filter(b => {
                                if (!searchOrderQuery.trim()) return true;
                                const q = searchOrderQuery.toLowerCase();
                                return (b.orderCode || '').toLowerCase().includes(q) ||
                                       (b.customerName || '').toLowerCase().includes(q) ||
                                       (b.customerPhone || '').toLowerCase().includes(q);
                            })
                            .map((b) => (
                                <div key={b.id} className="order-table-row">
                                    <strong style={{ color: '#241A16' }}>#{b.orderCode}</strong>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{b.customerName}</div>
                                        <div style={{ fontSize: '12px', color: '#8B7A6D' }}>{b.customerPhone}</div>
                                    </div>
                                    <div style={{ color: '#5C4A3F', fontSize: '12.5px' }}>
                                        {parseItemsSummary(b.items)}
                                    </div>
                                    <div>
                                        <strong>{formatCurrency(b.totalAmount)}</strong>
                                        {b.paymentMethod === 'pay_later' && (
                                            <div style={{ fontSize: '10.5px', color: '#B45309', fontWeight: 600 }}>
                                                <i className="fa-regular fa-clock" style={{ marginRight: 3 }} /> Thanh toán sau
                                            </div>
                                        )}
                                        {renderPayTag(b)}
                                    </div>
                                    <div>
                                        <select
                                            className="status-select-badge"
                                            value={b.status || 'Chờ chuyển khoản'}
                                            onChange={(e) => handleStatusChange(b.id, e.target.value)}
                                        >
                                            <option value="Chờ chuyển khoản">Chờ chuyển khoản</option>
                                            <option value="Chờ thanh toán">Chờ thanh toán (Sau)</option>
                                            <option value="Đã thanh toán">Đã thanh toán</option>
                                            <option value="Đang làm bánh">Đang làm bánh</option>
                                            <option value="Đang giao hàng">Đang giao hàng</option>
                                            <option value="Đã giao hàng">Đã giao hàng</option>
                                            <option value="Đã hủy">Đã hủy</option>
                                        </select>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-outline-paper"
                                        style={{ padding: '5px 10px', fontSize: '12px' }}
                                        onClick={() => setDetailBill(b)}
                                    >
                                        Chi tiết
                                    </button>
                                </div>
                            ))}
                    </div>
                )}

                {/* 3. PRODUCTS MANAGEMENT */}
                {currentTab === 'products' && (
                    <div>
                        <div className="admin-top-bar">
                            <div>
                                <h2 style={{ margin: '0 0 6px', fontFamily: 'Playfair Display, serif', fontSize: '30px', fontWeight: 700, color: '#241A16' }}>
                                    Quản lý menu &amp; hộp quà ({products.length})
                                </h2>
                                <p style={{ margin: 0, fontSize: '13.5px', color: '#7A6A5F' }}>
                                    Thêm mới, đổi giá, cập nhật tình trạng nhận đơn cho mùa Trung Thu 2026
                                </p>
                            </div>
                            <button type="button" className="btn-lacquer" onClick={() => handleOpenProductModal()}>
                                <i className="fa-solid fa-plus" style={{ marginRight: '6px' }}></i>
                                Thêm sản phẩm mới
                            </button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '18px' }}>
                            {products.map((p) => (
                                <div key={p.id} style={{ background: '#FFFDF7', border: '1px solid #E6D9C3', borderRadius: '4px', padding: '16px', display: 'flex', gap: '14px' }}>
                                    <img
                                        src={getImageUrl(p.imageUrl)}
                                        alt={p.name}
                                        style={{ width: '80px', height: '80px', borderRadius: '4px', objectFit: 'cover' }}
                                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/imgs/banh-trung-thu-thap-cam-lap-xuong.jpg'; }}
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#142C54', textTransform: 'uppercase' }}>
                                            {p.category || 'Bánh nướng'}
                                        </div>
                                        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', fontWeight: 700, color: '#241A16', margin: '3px 0' }}>
                                            {p.name}
                                        </div>
                                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#142C54', marginBottom: '8px' }}>
                                            {formatCurrency(p.price)}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                type="button"
                                                className="btn-outline-paper"
                                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                                onClick={() => handleOpenProductModal(p)}
                                            >
                                                Sửa
                                            </button>
                                            <button
                                                type="button"
                                                style={{ background: 'none', border: 'none', color: '#A08A6B', cursor: 'pointer' }}
                                                onClick={() => handleDeleteProduct(p.id)}
                                            >
                                                Xoá
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 4. SEPAY & STORE SETTINGS CONFIGURATION */}
                {currentTab === 'sepay' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', maxWidth: '780px' }}>
                        {/* ORDER INTAKE SETTINGS CARD */}
                        <div style={{ background: '#FFFDF7', border: '1px solid #E6D9C3', borderRadius: '8px', padding: '28px', boxShadow: '0 2px 10px rgba(36, 26, 22, 0.04)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                <div>
                                    <h3 style={{ margin: '0 0 6px', fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#241A16' }}>
                                        Trạng thái nhận đơn &amp; Thông báo khách hàng
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#6B5A4E' }}>
                                        Bật/tắt tiếp nhận đơn đặt trước từ website và cấu hình thông điệp lịch sự gửi đến khách hàng khi tạm ngưng.
                                    </p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: orderEnabled ? '#E8F6EF' : '#FFF1F0', border: `1px solid ${orderEnabled ? '#A3E0C0' : '#FFA39E'}`, padding: '8px 14px', borderRadius: '8px' }}>
                                    <span className={`admin-order-status-dot ${orderEnabled ? 'online' : 'offline'}`} />
                                    <span style={{ fontSize: '13px', fontWeight: 700, color: orderEnabled ? '#1E7E4E' : '#CF1322' }}>
                                        {orderEnabled ? 'ĐANG MỞ ĐƠN' : 'TẠM ĐÓNG ĐƠN'}
                                    </span>
                                    <label className="admin-switch" style={{ marginLeft: '4px' }}>
                                        <input
                                            type="checkbox"
                                            checked={orderEnabled}
                                            onChange={handleToggleOrderIntake}
                                        />
                                        <span className="admin-switch-slider" />
                                    </label>
                                </div>
                            </div>

                            {/* PREVIEW OF CLIENT BANNER */}
                            <div style={{ marginBottom: '22px', marginTop: '14px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#B8862F', marginBottom: '8px' }}>
                                    <i className="fa-solid fa-eye" style={{ marginRight: 6 }} /> Khung xem trước (Khách thấy khi tiệm tạm đóng đơn):
                                </div>
                                <div className="order-paused-banner" style={{ margin: 0, borderStyle: 'dashed' }}>
                                    <div className="order-paused-header">
                                        <div className="order-paused-badge">
                                            <span className="paused-pulse-dot" />
                                            <span>TẠM DỪNG NHẬN ĐƠN MỚI</span>
                                        </div>
                                        <div className="order-paused-tag-sub">Giao diện khách hàng</div>
                                    </div>
                                    <h4 className="order-paused-title" style={{ fontSize: '17px', margin: '0 0 6px' }}>
                                        <i className="fa-solid fa-store-slash order-paused-icon" />
                                        {orderStatus.closedTitle || 'Tiệm Tạm Dừng Nhận Đơn Mới'}
                                    </h4>
                                    <p className="order-paused-reason" style={{ fontSize: '13px', margin: '0 0 10px' }}>
                                        {orderStatus.closedReason || 'Để đảm bảo chu đáo và chất lượng bánh phục vụ khách hàng, tiệm xin phép tạm ngưng nhận thêm đơn...'}
                                    </p>
                                    {orderStatus.reopenTime && (
                                        <div className="order-paused-time" style={{ padding: '6px 10px', fontSize: '12px', margin: 0 }}>
                                            <i className="fa-regular fa-clock" /> {orderStatus.reopenTime}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button
                                type="button"
                                className="btn-lacquer"
                                onClick={handleOpenOrderSettingsModal}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '9px 18px', fontSize: '13px' }}
                            >
                                <i className="fa-solid fa-pen-to-square" />
                                Tùy chỉnh thông điệp tạm ngưng nhận đơn
                            </button>
                        </div>

                        {/* SEPAY CONFIGURATION CARD */}
                        <div style={{ background: '#FFFDF7', border: '1px solid #E6D9C3', borderRadius: '8px', padding: '28px', boxShadow: '0 2px 10px rgba(36, 26, 22, 0.04)' }}>
                            <h3 style={{ margin: '0 0 8px', fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#241A16' }}>
                                Cấu hình SePay VietQR
                            </h3>
                            <p style={{ fontSize: '13px', color: '#6B5A4E', marginBottom: '24px' }}>
                                Thông tin tài khoản nhận thanh toán tự động qua cổng SePay. Khách chuyển khoản đúng nội dung sẽ tự động khớp đơn và chuyển trạng thái "Đã thanh toán".
                            </p>

                        <form onSubmit={handleSaveSepayConfig}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#4A3A32', display: 'block', marginBottom: '6px' }}>
                                    Ngân hàng (Mã ngân hàng e.g. MB, VCB, ACB, TPB)
                                </label>
                                <input
                                    type="text"
                                    className="form-input-custom"
                                    value={sepayConfig.bankShortName}
                                    onChange={(e) => setSepayConfig({ ...sepayConfig, bankShortName: e.target.value })}
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#4A3A32', display: 'block', marginBottom: '6px' }}>
                                    Số tài khoản ngân hàng
                                </label>
                                <input
                                    type="text"
                                    className="form-input-custom"
                                    value={sepayConfig.accountNumber}
                                    onChange={(e) => setSepayConfig({ ...sepayConfig, accountNumber: e.target.value })}
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#4A3A32', display: 'block', marginBottom: '6px' }}>
                                    Tên chủ tài khoản (In hoa không dấu)
                                </label>
                                <input
                                    type="text"
                                    className="form-input-custom"
                                    value={sepayConfig.accountHolder}
                                    onChange={(e) => setSepayConfig({ ...sepayConfig, accountHolder: e.target.value })}
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ fontSize: '12.5px', fontWeight: 600, color: '#4A3A32', display: 'block', marginBottom: '6px' }}>
                                    SePay Webhook Secret / Token
                                </label>
                                <input
                                    type="password"
                                    className="form-input-custom"
                                    value={sepayConfig.webhookToken}
                                    onChange={(e) => setSepayConfig({ ...sepayConfig, webhookToken: e.target.value })}
                                    placeholder="whsec_..."
                                />
                                <div style={{ fontSize: '11.5px', color: '#A08A6B', marginTop: '4px' }}>
                                    Webhook URL nhận thông báo thanh toán: <code>https://order.cookielabs.vn/BackEndCakeShop/payment/webhook</code>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="btn-lacquer"
                                style={{ padding: '12px 24px' }}
                                disabled={savingSepay}
                            >
                                {savingSepay ? 'Đang lưu...' : 'Lưu cấu hình SePay'}
                            </button>
                        </form>
                    </div>
                </div>
                )}

                {/* 5. CÔNG THỨC & ĐỊNH LƯỢNG LÀM BÁNH (CHO BẾP / XƯỞNG) */}
                {currentTab === 'recipe' && (
                    <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
                        <RecipeCalculator
                            bills={bills}
                            batchEligibleBills={batchEligibleBills}
                            availableDates={batchAvailableDates}
                            dateFilter={batchDateFilter}
                            setDateFilter={setBatchDateFilter}
                            slotFilter={batchSlotFilter}
                            setSlotFilter={setBatchSlotFilter}
                            statusFilter={batchStatusFilter}
                            setStatusFilter={setBatchStatusFilter}
                            orderFilter={batchOrderFilter}
                            setOrderFilter={setBatchOrderFilter}
                            batchSummary={batchSummary}
                            formatDateShort={formatDateShort}
                            countCakesInBill={countCakesInBill}
                            todayStr={todayStr}
                            tomorrowStr={tomorrowStr}
                            isBillPaid={isBillPaid}
                        />
                    </div>
                )}
            </main>

            {/* PRODUCT EDIT / ADD MODAL */}
            {showProductModal && (
                <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
                        <h4 style={{ margin: '0 0 16px', fontFamily: 'Playfair Display, serif', fontSize: '22px' }}>
                            {editingProduct ? 'Chỉnh sửa món bánh' : 'Thêm vị bánh mới'}
                        </h4>

                        <form onSubmit={handleSaveProduct}>
                            <div style={{ marginBottom: '12px' }}>
                                <label className="form-label-custom">Tên món bánh</label>
                                <input
                                    type="text"
                                    className="form-input-custom"
                                    value={productForm.name}
                                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                                    required
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                <div>
                                    <label className="form-label-custom">Dòng bánh</label>
                                    <select
                                        className="form-input-custom"
                                        value={productForm.category}
                                        onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                                    >
                                        <option value="Bánh nướng">Bánh nướng</option>
                                        <option value="Bánh dẻo">Bánh dẻo</option>
                                        <option value="Bánh chay">Bánh chay</option>
                                        <option value="Hộp quà sẵn">Hộp quà sẵn</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label-custom">Giá tiền (VNĐ)</label>
                                    <input
                                        type="number"
                                        className="form-input-custom"
                                        value={productForm.price}
                                        onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '12px' }}>
                                <label className="form-label-custom">Mô tả nguyên liệu / hương vị</label>
                                <textarea
                                    className="form-input-custom"
                                    rows="2"
                                    value={productForm.description}
                                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                                ></textarea>
                            </div>

                            <div style={{ marginBottom: '14px' }}>
                                <label className="form-label-custom" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Ảnh món bánh</span>
                                    {(previewUrl || productForm.imageUrl) && (
                                        <span style={{ fontSize: '11px', color: '#16A34A', fontWeight: 600 }}>
                                            <i className="fa-solid fa-circle-check" style={{ marginRight: 4 }} /> Đã chọn ảnh
                                        </span>
                                    )}
                                </label>

                                <div style={{ display: 'flex', gap: '14px', alignItems: 'center', background: '#F8FAFC', padding: '12px', borderRadius: '10px', border: '1px dashed #CBD5E1', marginBottom: '8px' }}>
                                    <div style={{ width: '68px', height: '68px', borderRadius: '8px', overflow: 'hidden', background: '#E2E8F0', flexShrink: 0, display: 'grid', placeItems: 'center', border: '1px solid #E2E8F0' }}>
                                        {(previewUrl || productForm.imageUrl) ? (
                                            <img
                                                src={previewUrl || getImageUrl(productForm.imageUrl)}
                                                alt="Preview"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    // Không tự đổi sang ảnh bánh cá nếu link chưa kịp tải
                                                    console.warn('Không thể tải preview ảnh:', e.currentTarget.src);
                                                }}
                                            />
                                        ) : (
                                            <i className="fa-regular fa-image" style={{ fontSize: '24px', color: '#94A3B8' }} />
                                        )}
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            <label
                                                htmlFor="prod-img-file"
                                                className="btn"
                                                style={{
                                                    background: '#142C54',
                                                    color: '#FFF',
                                                    fontSize: '12.5px',
                                                    padding: '7px 14px',
                                                    borderRadius: '6px',
                                                    cursor: uploadingImg ? 'not-allowed' : 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    fontWeight: 600,
                                                    opacity: uploadingImg ? 0.7 : 1
                                                }}
                                            >
                                                {uploadingImg ? (
                                                    <><i className="fa-solid fa-spinner fa-spin" /> Đang tải ảnh...</>
                                                ) : (
                                                    <><i className="fa-solid fa-cloud-arrow-up" /> Chọn ảnh từ máy</>
                                                )}
                                            </label>
                                            <input
                                                id="prod-img-file"
                                                type="file"
                                                accept="image/png, image/jpeg, image/jpg, image/webp"
                                                style={{ display: 'none' }}
                                                disabled={uploadingImg}
                                                onChange={handleImageUpload}
                                            />

                                            {(previewUrl || productForm.imageUrl) && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setProductForm(prev => ({ ...prev, imageUrl: '' }));
                                                        setPreviewUrl('');
                                                    }}
                                                    style={{ background: 'transparent', border: 'none', color: '#DC2626', fontSize: '12px', cursor: 'pointer', padding: '6px 8px' }}
                                                >
                                                    <i className="fa-solid fa-trash-can" style={{ marginRight: 4 }} /> Gỡ ảnh
                                                </button>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#64748B', marginTop: '6px' }}>
                                            Hỗ trợ ảnh PNG, JPG, WEBP từ máy tính hoặc điện thoại.
                                        </div>
                                    </div>
                                </div>

                                <input
                                    type="text"
                                    className="form-input-custom"
                                    style={{ fontSize: '12px', padding: '7px 10px' }}
                                    value={productForm.imageUrl}
                                    onChange={(e) => {
                                        setPreviewUrl('');
                                        setProductForm({ ...productForm, imageUrl: e.target.value });
                                    }}
                                    placeholder="Hoặc dán đường dẫn link ảnh trực tiếp (/imgs/... hoặc https://...)"
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label className="form-label-custom">Tình trạng nhận đặt</label>
                                <select
                                    className="form-input-custom"
                                    value={productForm.stock_status}
                                    onChange={(e) => setProductForm({ ...productForm, stock_status: e.target.value })}
                                >
                                    <option value="stock">Còn nhận đặt</option>
                                    <option value="low">Sắp kín lịch</option>
                                    <option value="outstock">Hết lịch nhận</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" className="btn-outline-paper" onClick={() => setShowProductModal(false)}>
                                    Hủy
                                </button>
                                <button type="submit" className="btn-lacquer">
                                    Lưu sản phẩm
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* DETAIL BILL MODAL */}
            {detailBill && (
                <div className="modal-overlay" onClick={() => setDetailBill(null)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', width: '95%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h4 style={{ margin: 0, fontFamily: 'Playfair Display, serif', fontSize: '22px' }}>
                                Chi tiết đơn #{detailBill.orderCode}
                            </h4>
                            <button type="button" onClick={() => setDetailBill(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <div style={{ fontSize: '13.5px', marginBottom: '16px', lineHeight: 1.6 }}>
                            <div>Khách hàng: <strong>{detailBill.customerName}</strong></div>
                            <div>Số điện thoại: <strong>{detailBill.customerPhone || 'Chưa có'}</strong></div>
                            <div>Địa chỉ nhận: {detailBill.customerAddress || 'Chưa có'}</div>
                            <div>Ngày giao: <strong>{detailBill.delivery_date ? formatDateShort(detailBill.delivery_date) : 'Chưa định ngày'}</strong> (Khung {detailBill.deliveryTimeSlot})</div>
                            <div>
                                Phương thức: <strong style={{ color: detailBill.paymentMethod === 'pay_later' ? '#B45309' : '#142C54' }}>
                                    {detailBill.paymentMethod === 'pay_later' ? 'Thanh toán sau (Chưa thanh toán ngay)' : detailBill.paymentMethod === 'deposit_30' ? 'Cọc 30%' : 'Chuyển khoản VietQR'}
                                </strong> ({isPartial(detailBill) ? 'Còn thiếu tiền chênh lệch' : isFullyPaid(detailBill) ? 'Đã thanh toán' : 'Chưa thanh toán'})
                            </div>
                            {detailBill.comment_user && <div style={{ color: '#142C54' }}>Ghi chú: {detailBill.comment_user}</div>}
                        </div>

                        <div style={{ background: '#FBF6EC', padding: '14px', borderRadius: '8px', marginBottom: '18px', border: '1px solid #E6D8BF' }}>
                            <div style={{ fontWeight: 700, fontSize: '13.5px', marginBottom: '10px', color: '#241A16' }}>
                                <i className="fa-solid fa-layer-group" style={{ color: '#B8862F', marginRight: '6px' }}></i> Danh sách sản phẩm &amp; vị trí bánh:
                            </div>
                            {renderBillItemsDetail(detailBill.items)}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '20px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 700 }}>Tổng thanh toán:</span>
                            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', fontWeight: 700, color: '#142C54' }}>
                                {formatCurrency(detailBill.totalAmount)}
                            </span>
                        </div>

                        {(isPartial(detailBill) || refundDueOf(detailBill) > 0) && (
                            <div className="pay-ledger">
                                <div className="pay-ledger-row"><span>Khách đã trả</span><strong>{formatCurrency(paidAmountOf(detailBill))}</strong></div>
                                {amountDueOf(detailBill) > 0 && (
                                    <div className="pay-ledger-row due"><span>Còn phải thu (chênh lệch đổi vị)</span><strong>{formatCurrency(amountDueOf(detailBill))}</strong></div>
                                )}
                                {refundDueOf(detailBill) > 0 && (
                                    <div className="pay-ledger-row refund"><span>Cần hoàn lại khách</span><strong>{formatCurrency(refundDueOf(detailBill))}</strong></div>
                                )}
                                <div className="pay-ledger-actions">
                                    {amountDueOf(detailBill) > 0 && (
                                        <button type="button" className="btn-outline-paper" disabled={settling} onClick={() => handleSettle('collected')}>
                                            <i className="fa-solid fa-hand-holding-dollar" /> Đã thu {formatCurrency(amountDueOf(detailBill))}
                                        </button>
                                    )}
                                    {refundDueOf(detailBill) > 0 && (
                                        <button type="button" className="btn-outline-paper" disabled={settling} onClick={() => handleSettle('refunded')}>
                                            <i className="fa-solid fa-rotate-left" /> Đã hoàn {formatCurrency(refundDueOf(detailBill))}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {changeLog.length > 0 && (
                            <div className="change-log" style={{ marginBottom: '16px' }}>
                                <div className="change-log-title"><i className="fa-solid fa-clock-rotate-left" /> Lịch sử thay đổi</div>
                                {changeLog.map((entry) => (
                                    <div key={entry.id} className="change-log-item">
                                        <div className="change-log-time">
                                            {new Date(entry.createdAt).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                                            {' · '}{CHANGE_LOG_LABELS[entry.action] || entry.action}
                                            {entry.action === 'flavor_change' && Number(entry.oldTotal) !== Number(entry.newTotal) && (
                                                <> · {formatCurrency(entry.oldTotal)} → {formatCurrency(entry.newTotal)}</>
                                            )}
                                        </div>
                                        <div className="change-log-summary">{entry.summary}</div>
                                        {entry.note && <div className="change-log-note">“{entry.note}”</div>}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                            {canChangeFlavor(detailBill) && (
                                <button type="button" className="btn-outline-paper" onClick={() => setFlavorBill(detailBill)}>
                                    <i className="fa-solid fa-arrows-rotate" style={{ marginRight: '6px' }}></i> Đổi vị
                                </button>
                            )}
                            <button type="button" className="btn-outline-paper" onClick={() => handleOpenBatchPrint('all', detailBill)}>
                                <i className="fa-solid fa-print" style={{ marginRight: '6px' }}></i> In đơn (A4)
                            </button>
                            <button type="button" className="btn-lacquer" onClick={() => setDetailBill(null)}>
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {flavorBill && (
                <FlavorChangeModal
                    bill={flavorBill}
                    products={products}
                    onClose={() => setFlavorBill(null)}
                    onBillRefreshed={(fresh) => {
                        replaceBill(fresh);
                        setFlavorBill(prev => (prev ? { ...prev, ...fresh } : prev));
                    }}
                    onApplied={(updated, result) => handleFlavorApplied(flavorBill, updated, result)}
                />
            )}

            {/* BATCH BILL PRINT MODAL */}
            <BatchBillPrintModal
                isOpen={showBatchPrintModal}
                onClose={() => {
                    setShowBatchPrintModal(false);
                    setBatchPrintSingleBill(null);
                }}
                bills={bills}
                initialDate={batchPrintDate}
                sepayConfig={sepayConfig}
                singleBill={batchPrintSingleBill}
            />

            {/* ORDER INTAKE SETTINGS MODAL */}
            {showOrderModal && (
                <div className="modal-overlay" onClick={() => setShowOrderModal(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid #E6D8BF', paddingBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <i className="fa-solid fa-store" style={{ color: '#B8862F', fontSize: '20px' }}></i>
                                <h4 style={{ margin: 0, fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#241A16' }}>
                                    Cài đặt trạng thái nhận đơn
                                </h4>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowOrderModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#8F7A69' }}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSaveOrderSettings}>
                            {/* Switch row */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: orderForm.orderEnabled ? '#E8F6EF' : '#FFF1F0', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', border: `1px solid ${orderForm.orderEnabled ? '#A3E0C0' : '#FFA39E'}` }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '14px', color: orderForm.orderEnabled ? '#1E7E4E' : '#CF1322' }}>
                                        {orderForm.orderEnabled ? '🟢 ĐANG MỞ NHẬN ĐƠN MỚI' : '🔴 ĐANG TẠM DỪNG NHẬN ĐƠN'}
                                    </div>
                                    <div style={{ fontSize: '12.5px', color: '#6B5A4E', marginTop: '2px' }}>
                                        {orderForm.orderEnabled ? 'Khách hàng có thể chọn bánh và gửi đơn hàng bình thường.' : 'Khách truy cập sẽ thấy banner thông báo tạm dừng và không thể bấm đặt bánh.'}
                                    </div>
                                </div>
                                <label className="admin-switch">
                                    <input
                                        type="checkbox"
                                        checked={orderForm.orderEnabled}
                                        onChange={(e) => setOrderForm({ ...orderForm, orderEnabled: e.target.checked })}
                                    />
                                    <span className="admin-switch-slider" />
                                </label>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label className="form-label-custom">Tiêu đề thông báo tạm dừng</label>
                                <input
                                    type="text"
                                    className="form-input-custom"
                                    value={orderForm.closedTitle}
                                    onChange={(e) => setOrderForm({ ...orderForm, closedTitle: e.target.value })}
                                    placeholder="Ví dụ: Tiệm Tạm Dừng Nhận Đơn Mới"
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label className="form-label-custom">Nội dung lời nhắn gửi tới khách hàng</label>
                                <textarea
                                    className="form-input-custom"
                                    rows={4}
                                    value={orderForm.closedReason}
                                    onChange={(e) => setOrderForm({ ...orderForm, closedReason: e.target.value })}
                                    placeholder="Giải thích lý do lịch sự (ví dụ: để đảm bảo chất lượng từng mẻ bánh tươi ngon nhất...)"
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label className="form-label-custom">Thời gian dự kiến mở lại nhận đơn</label>
                                <input
                                    type="text"
                                    className="form-input-custom"
                                    value={orderForm.reopenTime}
                                    onChange={(e) => setOrderForm({ ...orderForm, reopenTime: e.target.value })}
                                    placeholder="Ví dụ: Dự kiến mở lại vào 08:00 sáng mai hoặc liên hệ hotline..."
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '22px' }}>
                                <div>
                                    <label className="form-label-custom">Hotline hỗ trợ gấp</label>
                                    <input
                                        type="text"
                                        className="form-input-custom"
                                        value={orderForm.hotline}
                                        onChange={(e) => setOrderForm({ ...orderForm, hotline: e.target.value })}
                                        placeholder="0856.345.789"
                                    />
                                </div>
                                <div>
                                    <label className="form-label-custom">Link Zalo tư vấn</label>
                                    <input
                                        type="text"
                                        className="form-input-custom"
                                        value={orderForm.zaloLink}
                                        onChange={(e) => setOrderForm({ ...orderForm, zaloLink: e.target.value })}
                                        placeholder="https://zalo.me/0856345789"
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #E6D8BF', paddingTop: '16px' }}>
                                <button type="button" className="btn-outline-paper" onClick={() => setShowOrderModal(false)}>
                                    Đóng
                                </button>
                                <button type="submit" className="btn-lacquer" disabled={savingOrderStatus}>
                                    {savingOrderStatus ? 'Đang lưu...' : 'Lưu cài đặt'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}